/**
 * A2UIChat - A2A Protocol Chat Component (Canvas Layout)
 * 
 * Unified chat component that mirrors the Canvas UI layout:
 * - Left panel: Tasks, Cursors, Artifacts
 * - Center: Chat messages
 * - Right panel: Personas, Agents, Selection info
 * 
 * Supports:
 * - Task-based message history via tasks.get
 * - SSE streaming with adaptive indicators
 * - Full-screen and compact (1/3 panel) layouts
 * - Agent and Persona selection
 * - Entity autocomplete with inline badges
 * - MCP UI component rendering (iframe-based)
 * - A2UI declarative component rendering
 * 
 * @module A2UIChat
 */

import { browserAPI, storage } from '@merlin/lib/js/browser-api';
import { EntityInputGlobal } from '@merlin/search/global';
import { captureError, addBreadcrumb, setUser } from '../../../lib/js/sentry.js';
import './A2UIChat.css';
import { safeSetInnerHTML, safeInsertAdjacentHTML } from '@merlin/lib/js/trusted-types.js';
import { renderMarkdown, renderTextWithEntities, renderContentParts, parseToolSteps } from './messageRenderer.js';

// ============================================================================
// A2UI Component Catalog - Maps A2UI types to HTML renderers
// See: https://github.com/google/A2UI
// ============================================================================

/**
 * A2UI Component Catalog
 * Maps component types to render functions
 * Each renderer receives (component, data, renderChildren) and returns HTML string
 */
const A2UI_CATALOG = {
    // Card component - container with header/content
    card: (comp, data, renderChildren) => {
        const { title, subtitle, image, variant = 'default' } = comp.props || {};
        return `
            <div class="a2ui-comp-card a2ui-card-${variant}" data-component-id="${comp.id}">
                ${image ? `<div class="a2ui-card-image"><img src="${escapeHtml(image)}" alt="${escapeHtml(title || '')}"/></div>` : ''}
                ${title ? `<div class="a2ui-card-header">
                    <h3 class="a2ui-card-title">${escapeHtml(title)}</h3>
                    ${subtitle ? `<p class="a2ui-card-subtitle">${escapeHtml(subtitle)}</p>` : ''}
                </div>` : ''}
                <div class="a2ui-card-content">${renderChildren(comp.children)}</div>
            </div>
        `;
    },

    // Text component - simple text display
    text: (comp, data) => {
        const { content, variant = 'body', markdown = false } = comp.props || {};
        const text = content || '';
        return `<p class="a2ui-comp-text a2ui-text-${variant}" data-component-id="${comp.id}">${escapeHtml(text)}</p>`;
    },

    // Button component - clickable action
    button: (comp, data) => {
        const { label, variant = 'primary', disabled = false, icon } = comp.props || {};
        const eventAction = comp.events?.click || '';
        return `
            <button class="a2ui-comp-button a2ui-btn-${variant}" 
                    data-component-id="${comp.id}"
                    data-action="${escapeHtml(eventAction)}"
                    ${disabled ? 'disabled' : ''}>
                ${icon ? `<i data-lucide="${escapeHtml(icon)}"></i>` : ''}
                <span>${escapeHtml(label || 'Button')}</span>
            </button>
        `;
    },

    // Input / TextField component
    'text-field': (comp, data) => {
        const { label, placeholder, value = '', type = 'text', required = false } = comp.props || {};
        const eventAction = comp.events?.change || '';
        return `
            <div class="a2ui-comp-text-field" data-component-id="${comp.id}">
                ${label ? `<label class="a2ui-field-label">${escapeHtml(label)}${required ? ' *' : ''}</label>` : ''}
                <input type="${escapeHtml(type)}" 
                       class="a2ui-field-input"
                       placeholder="${escapeHtml(placeholder || '')}"
                       value="${escapeHtml(value)}"
                       data-action="${escapeHtml(eventAction)}"
                       ${required ? 'required' : ''} />
            </div>
        `;
    },

    // Select / Dropdown component
    select: (comp, data) => {
        const { label, options = [], value, placeholder } = comp.props || {};
        const eventAction = comp.events?.change || '';
        const optionsHtml = options.map(opt => {
            const optValue = typeof opt === 'string' ? opt : opt.value;
            const optLabel = typeof opt === 'string' ? opt : opt.label;
            return `<option value="${escapeHtml(optValue)}" ${optValue === value ? 'selected' : ''}>${escapeHtml(optLabel)}</option>`;
        }).join('');
        return `
            <div class="a2ui-comp-select" data-component-id="${comp.id}">
                ${label ? `<label class="a2ui-field-label">${escapeHtml(label)}</label>` : ''}
                <select class="a2ui-select-input" data-action="${escapeHtml(eventAction)}">
                    ${placeholder ? `<option value="" disabled ${!value ? 'selected' : ''}>${escapeHtml(placeholder)}</option>` : ''}
                    ${optionsHtml}
                </select>
            </div>
        `;
    },

    // Image component
    image: (comp, data) => {
        const { src, alt = '', width, height } = comp.props || {};
        const style = [];
        if (width) style.push(`width: ${width}px`);
        if (height) style.push(`height: ${height}px`);
        return `<img class="a2ui-comp-image" data-component-id="${comp.id}" 
                     src="${escapeHtml(src || '')}" 
                     alt="${escapeHtml(alt)}"
                     ${style.length ? `style="${style.join(';')}"` : ''} />`;
    },

    // Divider / Separator
    divider: (comp) => `<hr class="a2ui-comp-divider" data-component-id="${comp.id}" />`,

    // Spacer
    spacer: (comp) => {
        const { height = 16 } = comp.props || {};
        return `<div class="a2ui-comp-spacer" data-component-id="${comp.id}" style="height: ${height}px"></div>`;
    },

    // Row / Horizontal layout
    row: (comp, data, renderChildren) => {
        const { gap = 8, align = 'center' } = comp.props || {};
        return `
            <div class="a2ui-comp-row" data-component-id="${comp.id}" style="gap: ${gap}px; align-items: ${align}">
                ${renderChildren(comp.children)}
            </div>
        `;
    },

    // Column / Vertical layout
    column: (comp, data, renderChildren) => {
        const { gap = 8, align = 'stretch' } = comp.props || {};
        return `
            <div class="a2ui-comp-column" data-component-id="${comp.id}" style="gap: ${gap}px; align-items: ${align}">
                ${renderChildren(comp.children)}
            </div>
        `;
    },

    // List component
    list: (comp, data, renderChildren) => {
        const { ordered = false } = comp.props || {};
        const tag = ordered ? 'ol' : 'ul';
        return `<${tag} class="a2ui-comp-list" data-component-id="${comp.id}">${renderChildren(comp.children)}</${tag}>`;
    },

    // List item
    'list-item': (comp, data, renderChildren) => {
        return `<li class="a2ui-comp-list-item" data-component-id="${comp.id}">${renderChildren(comp.children)}</li>`;
    },

    // Chip / Tag
    chip: (comp, data) => {
        const { label, color = 'default', removable = false } = comp.props || {};
        const eventAction = comp.events?.click || '';
        return `
            <span class="a2ui-comp-chip a2ui-chip-${color}" data-component-id="${comp.id}" data-action="${escapeHtml(eventAction)}">
                ${escapeHtml(label || '')}
                ${removable ? '<button class="a2ui-chip-remove">&times;</button>' : ''}
            </span>
        `;
    },

    // Progress indicator
    progress: (comp, data) => {
        const { value = 0, max = 100, label } = comp.props || {};
        const percent = Math.min(100, Math.max(0, (value / max) * 100));
        return `
            <div class="a2ui-comp-progress" data-component-id="${comp.id}">
                ${label ? `<span class="a2ui-progress-label">${escapeHtml(label)}</span>` : ''}
                <div class="a2ui-progress-bar">
                    <div class="a2ui-progress-fill" style="width: ${percent}%"></div>
                </div>
                <span class="a2ui-progress-value">${Math.round(percent)}%</span>
            </div>
        `;
    },

    // Icon component
    icon: (comp) => {
        const { name, size = 16 } = comp.props || {};
        return `<i class="a2ui-comp-icon" data-lucide="${escapeHtml(name || 'circle')}" data-component-id="${comp.id}" style="width:${size}px;height:${size}px"></i>`;
    },

    // Link component
    link: (comp, data) => {
        const { href, label, target = '_blank' } = comp.props || {};
        return `<a class="a2ui-comp-link" data-component-id="${comp.id}" href="${escapeHtml(href || '#')}" target="${target}">${escapeHtml(label || href || 'Link')}</a>`;
    },

    // Code block
    code: (comp, data) => {
        const { content, language = '' } = comp.props || {};
        return `<pre class="a2ui-comp-code" data-component-id="${comp.id}" data-language="${escapeHtml(language)}"><code>${escapeHtml(content || '')}</code></pre>`;
    },

    // Badge component
    badge: (comp, data) => {
        const { label, color = 'default' } = comp.props || {};
        return `<span class="a2ui-comp-badge a2ui-badge-${color}" data-component-id="${comp.id}">${escapeHtml(label || '')}</span>`;
    },

    // Unknown/fallback component
    _fallback: (comp) => {
        return `<div class="a2ui-comp-unknown" data-component-id="${comp.id}">[Unknown: ${escapeHtml(comp.type)}]</div>`;
    },
};

/**
 * Render an A2UI payload to HTML
 * @param {Object} payload - A2UI payload with components array
 * @param {Object} catalog - Component catalog (defaults to A2UI_CATALOG)
 * @returns {string} Rendered HTML
 */
function renderA2UIPayload(payload, catalog = A2UI_CATALOG) {
    if (!payload || !payload.components || !Array.isArray(payload.components)) {
        return '<div class="a2ui-render-error">Invalid A2UI payload</div>';
    }

    // Build component map for quick lookup
    const componentMap = new Map();
    payload.components.forEach(comp => componentMap.set(comp.id, comp));

    // Find root components (not referenced as children by any other component)
    const childIds = new Set();
    payload.components.forEach(comp => {
        if (comp.children) {
            comp.children.forEach(id => childIds.add(id));
        }
    });
    const rootComponents = payload.components.filter(comp => !childIds.has(comp.id));

    // Recursive render function
    const renderComponent = (comp) => {
        if (!comp) return '';
        const renderer = catalog[comp.type] || catalog._fallback;
        return renderer(comp, payload.data || {}, renderChildren);
    };

    // Render children by IDs
    const renderChildren = (childIds) => {
        if (!childIds || !Array.isArray(childIds)) return '';
        return childIds.map(id => {
            const child = componentMap.get(id);
            return child ? renderComponent(child) : '';
        }).join('');
    };

    // Render all root components
    return rootComponents.map(renderComponent).join('');
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// ============================================================================
// MCP UI Rendering - iframe-based UI components
// See: https://github.com/MCP-UI-Org/mcp-ui
// ============================================================================

/**
 * Render MCP UI Resource to HTML
 * @param {Object} artifact - MCP UI artifact with ui_resource
 * @param {Function} onAction - Callback for UI actions
 * @returns {string} Rendered HTML
 */
function renderMCPUIArtifact(artifact, onAction) {
    if (!artifact || !artifact.ui_resource) {
        return '<div class="a2ui-render-error">Invalid MCP UI artifact</div>';
    }

    const { uri, mime_type, text, blob } = artifact.ui_resource;
    const artifactId = artifact.id || crypto.randomUUID();
    
    // Determine content type and render accordingly
    if (mime_type === 'text/uri-list' && text) {
        // External URL - render as link or iframe
        return `
            <div class="a2ui-mcp-ui-container" data-artifact-id="${artifactId}" data-uri="${escapeHtml(uri)}">
                <div class="a2ui-mcp-ui-header">
                    <i data-lucide="external-link"></i>
                    <span>${escapeHtml(artifact.name || 'External UI')}</span>
                    <a href="${escapeHtml(text)}" target="_blank" class="a2ui-mcp-ui-open">Open</a>
                </div>
                <iframe class="a2ui-mcp-ui-frame" 
                        src="${escapeHtml(text)}" 
                        sandbox="allow-scripts allow-same-origin allow-forms"
                        loading="lazy"></iframe>
            </div>
        `;
    } else if (mime_type?.startsWith('text/html') && (text || blob)) {
        // Inline HTML - render in sandboxed iframe via srcdoc
        const htmlContent = text || (blob ? atob(blob) : '');
        // Use srcdoc for inline HTML (safer than data URI)
        return `
            <div class="a2ui-mcp-ui-container" data-artifact-id="${artifactId}" data-uri="${escapeHtml(uri)}">
                <div class="a2ui-mcp-ui-header">
                    <i data-lucide="layout"></i>
                    <span>${escapeHtml(artifact.name || 'UI Component')}</span>
                </div>
                <iframe class="a2ui-mcp-ui-frame" 
                        srcdoc="${escapeHtml(htmlContent)}" 
                        sandbox="allow-scripts allow-forms"
                        loading="lazy"></iframe>
            </div>
        `;
    } else if (mime_type === 'application/vnd.mcp-ui.remote-dom' && text) {
        // Remote DOM - advanced scripting (requires special handling)
        return `
            <div class="a2ui-mcp-ui-container a2ui-mcp-remote-dom" data-artifact-id="${artifactId}" data-uri="${escapeHtml(uri)}">
                <div class="a2ui-mcp-ui-header">
                    <i data-lucide="terminal"></i>
                    <span>${escapeHtml(artifact.name || 'Remote DOM')}</span>
                </div>
                <div class="a2ui-mcp-remote-dom-content" data-script="${escapeHtml(text)}">
                    <div class="a2ui-mcp-remote-dom-placeholder">
                        <i data-lucide="loader"></i>
                        <span>Loading remote UI...</span>
                    </div>
                </div>
            </div>
        `;
    }
    
    // Fallback for unknown types
    return `
        <div class="a2ui-mcp-ui-container a2ui-mcp-ui-fallback" data-artifact-id="${artifactId}">
            <div class="a2ui-mcp-ui-header">
                <i data-lucide="file-question"></i>
                <span>${escapeHtml(artifact.name || 'UI Resource')}</span>
            </div>
            <div class="a2ui-mcp-ui-info">
                <code>URI: ${escapeHtml(uri || 'N/A')}</code>
                <code>Type: ${escapeHtml(mime_type || 'unknown')}</code>
            </div>
        </div>
    `;
}

/**
 * Render A2UI artifact to HTML
 * @param {Object} artifact - A2UI artifact with payload
 * @returns {string} Rendered HTML
 */
function renderA2UIArtifact(artifact) {
    if (!artifact || !artifact.payload) {
        return '<div class="a2ui-render-error">Invalid A2UI artifact</div>';
    }

    const artifactId = artifact.id || crypto.randomUUID();
    const renderedContent = renderA2UIPayload(artifact.payload);
    
    return `
        <div class="a2ui-a2ui-container" data-artifact-id="${artifactId}" data-catalog="${escapeHtml(artifact.catalog || 'standard')}">
            <div class="a2ui-a2ui-header">
                <i data-lucide="component"></i>
                <span>${escapeHtml(artifact.name || 'UI Component')}</span>
            </div>
            <div class="a2ui-a2ui-content">
                ${renderedContent}
            </div>
        </div>
    `;
}

/**
 * Chat message bubble component
 * Supports text (with markdown + entity chips), MCP UI, A2UI,
 * and collapsible tool_call / tool_response parts.
 */
function createMessageBubble(message, layoutMode = 'compact') {
    const { role, timestamp, metadata } = message;
    // Normalize: accept both 'content' and 'parts' (backend history uses parts)
    const content = message.content || message.parts;
    const isUser = role === 'user';
    const agentName = metadata?.agent_name || 'Agent';
    // Use persona name for user messages, fallback to 'You'
    const userName = metadata?.persona_name || 'You';
    
    // Process content parts
    let renderedContent = '';
    
    if (Array.isArray(content)) {
        // Separate renderable parts into groups
        const textAndToolParts = [];   // text / tool_call / tool_response
        const otherPartsHtml = [];     // mcp_ui, a2ui, image, file

        for (const part of content) {
            const partType = part.type || part.kind || 'text';

            if (partType === 'text' || partType === 'tool_call' || partType === 'tool_response' || partType === 'transfer') {
                textAndToolParts.push(part);
            } else if (partType === 'mcp_ui' && part.ui_resource) {
                otherPartsHtml.push(`<div class="a2ui-message-ui">${renderMCPUIArtifact({ 
                    ui_resource: part.ui_resource,
                    name: part.name || 'UI Component',
                    id: part.id || crypto.randomUUID()
                })}</div>`);
            } else if (partType === 'a2ui' && part.payload) {
                otherPartsHtml.push(`<div class="a2ui-message-ui">${renderA2UIArtifact({
                    payload: part.payload,
                    name: part.name || 'UI Component',
                    catalog: part.catalog || 'standard',
                    id: part.id || crypto.randomUUID()
                })}</div>`);
            } else if (partType === 'image') {
                const src = part.url || (part.base64 ? `data:${part.mime_type || 'image/png'};base64,${part.base64}` : '');
                otherPartsHtml.push(`<div class="a2ui-message-image"><img src="${escapeHtml(src)}" alt="${escapeHtml(part.description || '')}" /></div>`);
            } else if (partType === 'file') {
                otherPartsHtml.push(`<div class="a2ui-message-file">
                    <i data-lucide="file"></i>
                    <span>${escapeHtml(part.filename || 'file')}</span>
                </div>`);
            }
        }

        // If we only have text parts (no tool_call/tool_response from backend),
        // check if the text contains legacy tool-call patterns and parse them
        const hasStructuredTools = textAndToolParts.some(p => 
            (p.type || p.kind) === 'tool_call' || (p.type || p.kind) === 'tool_response'
        );

        let processedParts = textAndToolParts;
        if (!hasStructuredTools && !isUser) {
            // Legacy fallback: parse tool patterns from concatenated text
            const allText = textAndToolParts.map(p => p.text || '').join('');
            if (allText.match(/Calling tool:|Tool response:|Transferring to agent:/)) {
                processedParts = parseToolSteps(allText);
            }
        }

        // --- Tool visibility filter (part level) ---
        // Hide ALL tool_call / tool_response parts so only text / UI content remains.
        if (!isUser) {
            processedParts = processedParts.filter(p => {
                const k = p.type || p.kind || 'text';
                return k !== 'tool_call' && k !== 'tool_response';
            });
        }

        // Render text + tool parts via the content renderer
        renderedContent = renderContentParts(processedParts, { isUser });
        // Append other parts
        renderedContent += otherPartsHtml.join('');
    } else if (typeof content === 'string') {
        if (isUser) {
            renderedContent = `<div class="a2ui-message-text">${renderTextWithEntities(content)}</div>`;
        } else {
            // Agent string content: check for legacy tool patterns
            if (content.match(/Calling tool:|Tool response:|Transferring to agent:/)) {
                // Parse then strip ALL tool parts — only keep text
                const parsed = parseToolSteps(content).filter(p => {
                    const k = p.kind || p.type || 'text';
                    return k !== 'tool_call' && k !== 'tool_response';
                });
                renderedContent = renderContentParts(parsed, { isUser: false });
            } else {
                renderedContent = `<div class="a2ui-message-text a2ui-markdown">${renderMarkdown(content)}</div>`;
            }
        }
    }
    
    const timeStr = timestamp 
        ? new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    // Explorer "Go to" navigation button (only for agent messages with directive)
    let explorerBtnHtml = '';
    if (!isUser && metadata?.explorer_action) {
        const ea = metadata.explorer_action;
        const label = explorerActionLabel(ea);
        explorerBtnHtml = `
            <div class="a2ui-explorer-action">
                <button class="a2ui-explorer-navigate-btn" data-explorer-action='${JSON.stringify(ea).replace(/'/g, '&#39;')}'>
                    <i data-lucide="compass"></i>
                    <span>${escapeHtml(label)}</span>
                </button>
            </div>`;
    }

    // If agent message has no visible content after filtering, skip it entirely
    if (!isUser && !renderedContent.trim() && !explorerBtnHtml) {
        return '';
    }

    return `
        <div class="a2ui-message ${isUser ? 'user' : 'agent'}" data-layout="${layoutMode}">
            <div class="a2ui-message-header">
                <span class="a2ui-message-time">${timeStr}</span>
                <span class="a2ui-message-role">${isUser ? userName : agentName}</span>
                <div class="a2ui-avatar ${isUser ? 'user' : 'agent'}">
                    <i data-lucide="${isUser ? 'user' : 'bot'}"></i>
                </div>
            </div>
            <div class="a2ui-message-content">
                ${renderedContent}
                ${explorerBtnHtml}
            </div>
        </div>
    `;
}

// ============================================
// Explorer directive helpers
// ============================================
const EXPLORER_DIRECTIVE_RE = /<!--\s*explorer:(.*?)-->/s;

/**
 * Extract an `<!-- explorer:{...} -->` directive from text.
 * Returns { cleanText, action } where action is the parsed JSON or null.
 */
function extractExplorerDirective(text) {
    const m = EXPLORER_DIRECTIVE_RE.exec(text);
    if (!m) return { cleanText: text, action: null };
    try {
        const payload = JSON.parse(m[1].trim());
        if (payload && typeof payload === 'object' && payload.tab) {
            return {
                cleanText: text.slice(0, m.index).trimEnd() + text.slice(m.index + m[0].length),
                action: payload,
            };
        }
    } catch (_) { /* malformed JSON — ignore */ }
    return { cleanText: text, action: null };
}

/**
 * Build the label for an explorer navigate button.
 */
function explorerActionLabel(action) {
    if (action.label) return action.label;
    const tabLabels = { graph: 'Graph', bookmarks: 'Bookmarks', timeline: 'Timeline', analytics: 'Analytics', artefacts: 'Artefacts' };
    const tab = tabLabels[action.tab] || action.tab;
    const target = action.target ? `: ${action.target}` : '';
    return `Go to ${tab}${target}`;
}

/**
 * Error message bubble component - shows agent name for errors
 */
function createErrorBubble(errorMessage, agentName, timestamp, layoutMode = 'compact') {
    const timeStr = timestamp 
        ? new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    return `
        <div class="a2ui-message error" data-layout="${layoutMode}">
            <div class="a2ui-message-header">
                <span class="a2ui-message-time">${timeStr}</span>
                <span class="a2ui-message-role">${escapeHtml(agentName)}</span>
                <div class="a2ui-avatar error">
                    <i data-lucide="alert-circle"></i>
                </div>
            </div>
            <div class="a2ui-message-content a2ui-error-content">
                <i data-lucide="alert-triangle"></i>
                <span>${escapeHtml(errorMessage)}</span>
            </div>
        </div>
    `;
}

/**
 * Streaming indicator component
 */
function createStreamingIndicator(layoutMode = 'compact') {
    return `
        <div class="a2ui-streaming-indicator ${layoutMode}">
            <div class="a2ui-typing-dots">
                <span></span>
                <span></span>
                <span></span>
            </div>
        </div>
    `;
}

// Maximum number of open tabs (including Main)
const MAX_TABS = 6;

/**
 * A2UIChat Class - Main chat component with Canvas layout
 */
export class A2UIChat {
    constructor(options = {}) {
        this.options = {
            apiEndpoint: null,
            workspaceId: null,
            canvasId: null,
            accessToken: null,
            layoutMode: 'compact', // 'compact' or 'full'
            theme: 'dark',
            onMessageSent: null,
            onTaskCreated: null,
            onError: null,
            onEntityBadgeClick: null,
            onReset: null,  // Called when user clears conversation / resets chat
            onDataChanged: null, // Called when agent completes — parent should reload graph data
            ...options
        };
        
        this.container = null;
        this.messages = [];
        
        // Data collections
        this.tasks = [];
        this.artifacts = [];
        this.personas = [];
        this.agents = [];
        
        // Selection state
        this.selectedPersonaId = null;
        this.selectedPersonaName = null;
        this.selectedAgentId = null;
        this.currentTaskId = null;
        this.selectedCanvasId = null;
        this.selectedCursorId = null;
        
        // Tab state (not persisted - resets on page reload)
        // Each tab: { id: string, type: 'main'|'task', name: string, taskId?: string }
        this.openTabs = [{ id: 'main', type: 'main', name: 'New task' }];
        this.activeTabId = 'main';
        
        // Message cache per tab (taskId -> messages[])
        this.tabMessages = {};
        
        // Collapsed sections state (section name -> expanded boolean)
        this._expandedSections = {};
        
        // Cursor state
        this.cursorId = null;
        this.cursorName = 'Me';
        
        // Entity input component
        this.entityInput = null;
        
        this.isStreaming = false;
        this.eventSource = null;
        
        this._initialized = false;
    }
    
    /**
     * Initialize the chat component
     */
    async init(containerElement) {
        this.container = containerElement;
        
        console.log('[A2UIChat] Initializing with:', {
            apiEndpoint: this.options.apiEndpoint,
            workspaceId: this.options.workspaceId,
            canvasId: this.options.canvasId,
            hasToken: !!this.options.accessToken
        });
        
        // Load saved state
        await this._loadState();
        
        // Render initial UI
        this._render();
        
        // Setup event handlers
        this._setupEventHandlers();
        
        // Setup collapsible section toggles
        this._setupSectionToggles();
        
        // Load all data in parallel
        await Promise.all([
            this._loadTasks(),
            this._loadArtifacts(),
            this._loadAgents(),
            this._loadDefaultCursor(),
        ]);
        
        // Load messages for the active tab (Main tab shows all history)
        await this._loadTabMessages();
        
        this._initialized = true;
        
        // Refresh icons
        this._refreshIcons();
        
        return this;
    }
    
    /**
     * Load saved state from storage
     */
    _stateKey(key) {
        // Workspace-scoped storage keys prevent stale cross-workspace IDs
        const wsId = this.options.workspaceId;
        return wsId ? `a2ui_${wsId}_${key}` : `a2ui_${key}`;
    }

    async _loadState() {
        try {
            // Keys that are workspace-specific (canvas, cursor, task belong to a workspace)
            const wsKeys = [
                this._stateKey('selected_agent'),
                this._stateKey('selected_persona'),
                this._stateKey('current_task'),
                this._stateKey('selected_canvas'),
                this._stateKey('selected_cursor'),
            ];
            // Also read legacy global keys for backward compatibility
            const globalKeys = [
                'a2ui_selected_agent',
                'a2ui_selected_persona',
                'a2ui_current_task',
                'a2ui_selected_canvas',
                'a2ui_selected_cursor',
            ];
            const data = await storage.get([...wsKeys, ...globalKeys]);

            // Prefer workspace-scoped values, fall back to global
            this.selectedAgentId  = data[this._stateKey('selected_agent')]  || data.a2ui_selected_agent  || null;
            this.selectedPersonaId = data[this._stateKey('selected_persona')] || data.a2ui_selected_persona || null;
            this.currentTaskId    = data[this._stateKey('current_task')]    || null;  // task is ALWAYS workspace-scoped
            this.selectedCanvasId = data[this._stateKey('selected_canvas')] || null;  // canvas is ALWAYS workspace-scoped
            this.selectedCursorId = data[this._stateKey('selected_cursor')] || null;  // cursor is ALWAYS workspace-scoped
        } catch (err) {
            console.warn('[A2UIChat] Failed to load state:', err);
        }
    }
    
    /**
     * Save state to storage
     */
    async _saveState() {
        try {
            await storage.set({
                // Save workspace-scoped keys
                [this._stateKey('selected_agent')]: this.selectedAgentId,
                [this._stateKey('selected_persona')]: this.selectedPersonaId,
                [this._stateKey('current_task')]: this.currentTaskId,
                [this._stateKey('selected_canvas')]: this.selectedCanvasId,
                [this._stateKey('selected_cursor')]: this.selectedCursorId,
                // Also update legacy global keys for agent/persona (not workspace-specific)
                a2ui_selected_agent: this.selectedAgentId,
                a2ui_selected_persona: this.selectedPersonaId,
            });
        } catch (err) {
            console.warn('[A2UIChat] Failed to save state:', err);
        }
    }
    
    /**
     * Render the full Canvas-style layout
     */
    _render() {
        const { layoutMode, theme } = this.options;
        
        safeSetInnerHTML(this.container, `
            <div class="a2ui-canvas" data-layout="${layoutMode}" data-theme="${theme}" style="height: 100%; width: 100%;">
                <!-- Left: Chat Area -->
                <div class="a2ui-chat-panel">
                    <div class="a2ui-chat-header">
                        <div class="a2ui-tab-bar" id="a2ui-tab-bar">
                            ${this._renderTabBar()}
                        </div>
                        <button class="a2ui-icon-btn" id="a2ui-new-chat" title="Clear history">
                            <i data-lucide="refresh-cw"></i>
                        </button>
                    </div>
                    
                    <div class="a2ui-chat-messages" id="a2ui-messages">
                        ${this._getEmptyState()}
                    </div>
                    
                    <div class="a2ui-chat-input">
                        <div class="a2ui-input-wrapper">
                            <div id="a2ui-entity-input-container"></div>
                            <button class="a2ui-icon-btn" id="a2ui-voice-btn" title="Voice input">
                                <i data-lucide="mic"></i>
                            </button>
                            <button id="a2ui-send" class="a2ui-send-btn" title="Send message">
                                <i data-lucide="send"></i>
                            </button>
                        </div>
                    </div>
                </div>
                
                <!-- Right: Sidebar with Agent, Canvas, Cursor, Tasks, Artifacts -->
                <div class="a2ui-sidebar">
                    <!-- AGENT Section - Shows selected, others collapsed -->
                    <div class="a2ui-panel-section" data-section="agents">
                        <div class="a2ui-section-header" data-toggle="agents" data-action="toggle-agents" data-tooltip="Agents" role="button" tabindex="0">
                            <i data-lucide="chevron-down" class="a2ui-collapse-icon"></i>
                            <i data-lucide="bot"></i>
                            <span>Agent</span>
                        </div>
                        <div class="a2ui-section-content" id="a2ui-agents-list">
                            <div class="a2ui-loading">Loading...</div>
                        </div>
                    </div>
                    
                    <div class="a2ui-sidebar-divider"></div>
                    
                    <!-- CANVAS Section - Collapsed by default -->
                    <div class="a2ui-panel-section collapsed" data-section="canvas">
                        <div class="a2ui-section-header" data-toggle="canvas" data-action="toggle-canvas" data-tooltip="Canvas" role="button" tabindex="0">
                            <i data-lucide="chevron-right" class="a2ui-collapse-icon"></i>
                            <i data-lucide="layout-grid"></i>
                            <span>Canvas</span>
                        </div>
                        <div class="a2ui-section-content" id="a2ui-canvas-info">
                            <div class="a2ui-info-row">
                                <span class="a2ui-info-label">Name:</span>
                                <span class="a2ui-info-value">${this.options.canvasId ? 'Active' : 'None'}</span>
                            </div>
                            <div class="a2ui-info-row">
                                <span class="a2ui-info-label">Status:</span>
                                <span class="a2ui-status-badge available">AVAILABLE</span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- CURSOR Section - Collapsed by default -->
                    <div class="a2ui-panel-section collapsed" data-section="cursor">
                        <div class="a2ui-section-header" data-toggle="cursor" data-action="toggle-cursor" data-tooltip="Cursor" role="button" tabindex="0">
                            <i data-lucide="chevron-right" class="a2ui-collapse-icon"></i>
                            <i data-lucide="mouse-pointer-2"></i>
                            <span>Cursor</span>
                        </div>
                        <div class="a2ui-section-content" id="a2ui-cursor-info">
                            <div class="a2ui-info-row">
                                <span class="a2ui-info-label">Name:</span>
                                <span class="a2ui-info-value" id="a2ui-cursor-name">Me</span>
                            </div>
                            <div class="a2ui-info-row">
                                <span class="a2ui-info-label">Graph:</span>
                                <span class="a2ui-info-value" id="a2ui-cursor-stats">0 nodes, 0 edges</span>
                            </div>
                        </div>
                    </div>
                    
                    <div class="a2ui-sidebar-divider"></div>
                    
                    <!-- TASKS Section - Shows last 3, rest collapsed -->
                    <div class="a2ui-panel-section" data-section="tasks">
                        <div class="a2ui-section-header" data-toggle="tasks" data-action="toggle-tasks" data-tooltip="Tasks" role="button" tabindex="0">
                            <i data-lucide="chevron-down" class="a2ui-collapse-icon"></i>
                            <i data-lucide="list-todo"></i>
                            <span>Tasks</span>
                            <span class="a2ui-badge" id="a2ui-tasks-count">0</span>
                        </div>
                        <div class="a2ui-section-content" id="a2ui-tasks-list">
                            <div class="a2ui-empty-section">No tasks yet</div>
                        </div>
                    </div>
                    
                    <!-- ARTIFACTS Section - Collapsed by default -->
                    <div class="a2ui-panel-section collapsed" data-section="artifacts">
                        <div class="a2ui-section-header" data-toggle="artifacts" data-action="toggle-artifacts" data-tooltip="Artifacts" role="button" tabindex="0">
                            <i data-lucide="chevron-right" class="a2ui-collapse-icon"></i>
                            <i data-lucide="package"></i>
                            <span>Artifacts</span>
                            <span class="a2ui-badge" id="a2ui-artifacts-count">0</span>
                        </div>
                        <div class="a2ui-section-content" id="a2ui-artifacts-list">
                            <div class="a2ui-empty-section">No artifacts yet</div>
                        </div>
                    </div>
                </div>
            </div>
        `);
        
        // Auto-resize textarea
        const textarea = this.container.querySelector('#a2ui-input');
        if (textarea) {
            textarea.addEventListener('input', () => this._autoResize(textarea));
        }
    }
    
    /**
     * Get empty state HTML for chat - shows centered input
     */
    _getEmptyState() {
        return `
            <div class="a2ui-empty-state">
                <div class="a2ui-empty-input-wrapper">
                    <div id="a2ui-empty-entity-input-container"></div>
                    <button id="a2ui-empty-send" class="a2ui-send-btn" title="Send message">
                        <i data-lucide="send"></i>
                    </button>
                </div>
                <p class="a2ui-empty-hint">Type a message to start</p>
            </div>
        `;
    }
    
    /**
     * Auto-resize textarea
     */
    _autoResize(textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
    
    /**
     * Setup simple input fallback when EntityInput is not available
     */
    _setupSimpleInput(container) {
        safeSetInnerHTML(container, `
            <textarea 
                id="a2ui-simple-input" 
                class="a2ui-simple-textarea"
                placeholder="Type a message..."
                rows="1"
            ></textarea>
        `);
        
        const textarea = container.querySelector('#a2ui-simple-input');
        if (textarea) {
            // Auto-resize on input
            textarea.addEventListener('input', () => this._autoResize(textarea));
            
            // Handle Enter to send (Shift+Enter for newline)
            textarea.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    const content = textarea.value.trim();
                    if (content) {
                        this._handleSendFromEntityInput({ 
                            query: content, 
                            entities: [],
                            keywords: []
                        });
                        textarea.value = '';
                        this._autoResize(textarea);
                    }
                }
            });
            
            // Store reference for programmatic access
            this._simpleInput = textarea;
        }
    }
    
    /**
     * Refresh Lucide icons
     */
    _refreshIcons() {
        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }
    }
    
    /**
     * Setup event handlers
     */
    _setupEventHandlers() {
        // Bind methods to ensure correct context
        this._handleSend = this._handleSend.bind(this);
        this._renderMessages = this._renderMessages.bind(this);
        this._handleSuggestionInput = this._handleSuggestionInput.bind(this);
        
        // Initialize EntityInput component or fallback to simple textarea
        const entityInputContainer = this.container.querySelector('#a2ui-entity-input-container');
        if (entityInputContainer) {
            if (EntityInputGlobal) {
                // Use EntityInput component for rich autocomplete
                this.entityInput = new EntityInputGlobal({
                    placeholder: 'Type to search or ask a question...',
                    debounceMs: 300,
                    minSearchLength: 2,
                    theme: this.options.theme,
                    dropdownPosition: 'above',
                    onSearch: (query) => this._handleSuggestionInput(query),
                    onSubmit: (data) => this._handleSendFromEntityInput(data),
                    onEntitySelect: (entity) => {
                        console.log('[A2UIChat] Entity selected:', entity);
                    },
                    onChange: (data) => {
                        // Could be used for draft saving
                    },
                });
                this.entityInput.init(entityInputContainer);
            } else {
                // Fallback to simple textarea
                this._setupSimpleInput(entityInputContainer);
            }
        }
        
        // Send button
        const sendBtn = this.container.querySelector('#a2ui-send');
        if (sendBtn) {
            sendBtn.addEventListener('click', this._handleSend);
        }
        
        // Setup tab handlers
        this._setupTabHandlers();
        
        // New chat button
        const newChatBtn = this.container.querySelector('#a2ui-new-chat');
        if (newChatBtn) {
            newChatBtn.addEventListener('click', () => this.clearConversation());
        }
        
        // Entity badge click delegation
        if (this.options.onEntityBadgeClick) {
            this.container.addEventListener('click', (e) => {
                const badge = e.target.closest('.entity-badge');
                if (badge) {
                    e.preventDefault();
                    e.stopPropagation();
                    const entityId = badge.dataset.entityId;
                    const entityType = badge.dataset.entityType;
                    if (entityId) {
                        this.options.onEntityBadgeClick(entityId, entityType, e);
                    }
                }
            });
        }

        // Explorer navigate button click delegation
        this.container.addEventListener('click', (e) => {
            const btn = e.target.closest('.a2ui-explorer-navigate-btn');
            if (btn) {
                e.preventDefault();
                e.stopPropagation();
                try {
                    const action = JSON.parse(btn.dataset.explorerAction);
                    if (this.options.onExplorerNavigate) {
                        this.options.onExplorerNavigate(action);
                    }
                } catch (err) {
                    console.warn('[A2UIChat] Failed to parse explorer action:', err);
                }
            }
        });
    }
    
    // ========================================
    // DATA LOADING
    // ========================================
    
    /**
     * Load tasks for this canvas/workspace
     * Uses the /a2a/tasks endpoint (same as explorer-tasks)
     */
    async _loadTasks() {
        console.log('[A2UIChat] _loadTasks called, workspaceId:', this.options.workspaceId);
        
        // Allow empty string apiEndpoint (for proxied dev environment)
        if (!this.options.workspaceId || this.options.apiEndpoint === null || this.options.apiEndpoint === undefined) {
            console.warn('[A2UIChat] Cannot load tasks - missing workspaceId or apiEndpoint');
            this.tasks = [];
            this._renderTasks();
            return;
        }
        
        try {
            const canvasParam = this.options.canvasId ? `?canvas_id=${this.options.canvasId}` : '';
            const response = await this._apiRequest(
                `/workspaces/${this.options.workspaceId}/a2a/tasks${canvasParam}`
            );
            
            // Handle paginated response: { tasks: [...], total, skip, limit }
            this.tasks = Array.isArray(response) ? response : (response?.tasks || response?.items || []);
            console.log('[A2UIChat] Loaded tasks:', this.tasks.length);
            this._renderTasks();
        } catch (err) {
            console.warn('[A2UIChat] Failed to load tasks:', err);
            this.tasks = [];
            this._renderTasks();
        }
    }
    
    /**
     * Load artifacts for this canvas
     */
    async _loadArtifacts() {
        // Allow empty string apiEndpoint (for proxied dev environment)
        if (!this.options.workspaceId || this.options.apiEndpoint === null || this.options.apiEndpoint === undefined || !this.options.canvasId) {
            this.artifacts = [];
            this._renderArtifacts();
            return;
        }
        
        try {
            const response = await this._apiRequest(
                `/workspaces/${this.options.workspaceId}/canvas_artifacts/?canvas_id=${this.options.canvasId}`
            );
            
            // Handle paginated response
            this.artifacts = Array.isArray(response) ? response : (response?.items || response?.artifacts || []);
            this._renderArtifacts();
        } catch (err) {
            console.warn('[A2UIChat] Failed to load artifacts:', err);
            this.artifacts = [];
            this._renderArtifacts();
        }
    }
    
    /**
     * Set persona from external source (e.g., header selector)
     * @param {string} personaId - The persona ID
     * @param {string} [personaName] - The persona display name
     */
    setPersona(personaId, personaName = null) {
        this.selectedPersonaId = personaId;
        this.selectedPersonaName = personaName;
        this._saveState();
        console.log('[A2UIChat] Persona set to:', personaId, personaName);
    }
    
    /**
     * Set canvas from external source (e.g., Context tab selector)
     * @param {string} canvasId - The canvas ID
     */
    setCanvas(canvasId) {
        this.selectedCanvasId = canvasId || null;
        this._saveState();
        // Reload agents to filter by canvas whitelist
        this._loadAgents();
        console.log('[A2UIChat] Canvas set to:', canvasId);
    }
    
    /**
     * Set cursor from external source
     * @param {string} cursorId - The cursor ID
     */
    setCursor(cursorId) {
        this.selectedCursorId = cursorId || null;
        this._saveState();
        console.log('[A2UIChat] Cursor set to:', cursorId);
    }
    
    /**
     * Set workspace from external source (e.g., Context tab selector)
     * @param {string} workspaceId - The workspace ID
     * @param {string} apiEndpoint - Optional API endpoint (if changed)
     * @param {string} accessToken - Optional access token (if changed)
     */
    async setWorkspace(workspaceId, apiEndpoint = null, accessToken = null) {
        console.log('[A2UIChat] setWorkspace called:', workspaceId);
        
        this.options.workspaceId = workspaceId || null;
        
        // Update API endpoint and token if provided
        if (apiEndpoint) {
            this.options.apiEndpoint = apiEndpoint;
        }
        if (accessToken) {
            this.options.accessToken = accessToken;
        }
        
        // Reload data for the new workspace (allow empty string apiEndpoint for proxied dev)
        if (workspaceId && this.options.apiEndpoint !== null && this.options.apiEndpoint !== undefined) {
            console.log('[A2UIChat] Reloading data for workspace:', workspaceId);
            
            // Reset current data and conversation state from previous workspace
            this.tasks = [];
            this.agents = [];
            this.artifacts = [];
            this.messages = [];
            this.currentTaskId = null;
            // Clear workspace-specific selections (canvas/cursor belong to old workspace)
            this.selectedCanvasId = null;
            this.selectedCursorId = null;
            this.cursorId = null;
            this.cursorName = null;
            await this._saveState();
            
            // Reset tabs to clear task tabs from old workspace
            if (this.tabs) {
                this.tabs = [{ id: 'main', label: 'New Chat', active: true }];
                this.activeTabId = 'main';
            }
            
            // Show loading state
            this._renderTasks();
            this._renderAgents();
            this._renderArtifacts();
            this._renderMessages();
            if (this._renderTabs) this._renderTabs();
            
            // Load new data
            await Promise.all([
                this._loadTasks(),
                this._loadAgents(),
                this._loadDefaultCursor(),
            ]);
            
            console.log('[A2UIChat] Data reload complete');
        } else {
            // No workspace - show empty state
            this.tasks = [];
            this.agents = [];
            this.artifacts = [];
            this._renderTasks();
            this._renderAgents();
            this._renderArtifacts();
        }
    }
    
    /**
     * Load agents
     */
    async _loadAgents() {
        console.log('[A2UIChat] _loadAgents called, workspaceId:', this.options.workspaceId, 'apiEndpoint:', this.options.apiEndpoint);
        
        // Allow empty string apiEndpoint (for proxied dev environment)
        if (!this.options.workspaceId || this.options.apiEndpoint === null || this.options.apiEndpoint === undefined) {
            console.warn('[A2UIChat] Cannot load agents - missing workspaceId or apiEndpoint');
            this.agents = [];
            this._renderAgents();
            return;
        }
        
        try {
            const response = await this._apiRequest(
                `/workspaces/${this.options.workspaceId}/agents/`
            );
            
            console.log('[A2UIChat] Loaded agents:', response?.length || 0);
            let agents = response || [];
            
            // Filter by canvas whitelist if a canvas is selected
            if (this.selectedCanvasId) {
                try {
                    const canvas = await this._apiRequest(
                        `/workspaces/${this.options.workspaceId}/canvas/${this.selectedCanvasId}`
                    );
                    // Canvas data has AgentMerlin with agents array (whitelist)
                    const canvasData = canvas?.data;
                    if (canvasData && canvasData.agents && canvasData.agents.length > 0) {
                        const allowedAgents = canvasData.agents;
                        agents = agents.filter(a => allowedAgents.includes(a.id));
                        console.log('[A2UIChat] Filtered agents by canvas whitelist:', allowedAgents);
                    }
                } catch (err) {
                    console.warn('[A2UIChat] Failed to load canvas for filtering:', err);
                    // Clear stale canvas reference on 404 (canvas deleted or wrong workspace)
                    if (err.status === 404) {
                        this.selectedCanvasId = null;
                        await this._saveState();
                        console.log('[A2UIChat] Cleared stale selectedCanvasId after 404');
                    }
                }
            }
            
            this.agents = agents;
            
            // Auto-select first agent if none selected or if current is not in filtered list
            const currentAgentValid = this.selectedAgentId && this.agents.some(a => a.id === this.selectedAgentId);
            if (!currentAgentValid && this.agents.length > 0) {
                const defaultAgent = this.agents.find(a => a.is_system) || this.agents[0];
                if (defaultAgent) {
                    this.selectedAgentId = defaultAgent.id;
                    await this._saveState();
                }
            }
            
            this._renderAgents();
        } catch (err) {
            console.error('[A2UIChat] Failed to load agents:', err);
            if (this.options.onError) {
                this.options.onError(err);
            }
            this.agents = [];
            this._renderAgents();
        }
    }
    
    /**
     * Load task history (aligned with explorer-tasks pattern)
     */
    async _loadTaskHistory() {
        if (!this.currentTaskId || !this.options.workspaceId) return;
        
        try {
            const task = await this._apiRequest(
                `/workspaces/${this.options.workspaceId}/a2a/tasks/${this.currentTaskId}`
            );
            
            // Match explorer-tasks: response?.history || response?.messages
            const rawMessages = task?.history || task?.messages || [];
            
            if (rawMessages.length > 0) {
                // Resolve agent name and persona name from task metadata
                const agentName = this._resolveAgentName(task.agent_id);
                const personaName = this._resolvePersonaName(task.persona_id);
                
                // Enrich messages with agent_name / persona_name
                this.messages = rawMessages.map(msg => ({
                    ...msg,
                    metadata: {
                        ...msg.metadata,
                        ...(msg.role === 'user' && personaName ? { persona_name: personaName } : {}),
                        ...(msg.role !== 'user' && agentName ? { agent_name: agentName } : {}),
                    },
                }));
                this._renderMessages();
            }
        } catch (err) {
            console.error('[A2UIChat] Failed to load task history:', err);
            // Only clear currentTaskId for non-transient errors (e.g. 404).
            // Transient errors (502, network failures) should preserve the
            // task association so the tab can recover on the next attempt.
            const isTransient = !err.status || err.status >= 500 || err.message?.includes('NetworkError');
            if (!isTransient) {
                this.currentTaskId = null;
                await this._saveState();
            }
        }
    }
    
    // ========================================
    // RENDER METHODS
    // ========================================
    
    _renderTasks() {
        const container = this.container.querySelector('#a2ui-tasks-list');
        const badge = this.container.querySelector('#a2ui-tasks-count');
        
        if (badge) badge.textContent = this.tasks.length;
        
        if (!container) return;
        
        if (this.tasks.length === 0) {
            safeSetInnerHTML(container, '<div class="a2ui-empty-section">No tasks yet</div>');
            return;
        }
        
        // Sort tasks by created_at descending (newest first)
        const sortedTasks = [...this.tasks].sort((a, b) => {
            const dateA = new Date(a.created_at || a.createdAt || 0);
            const dateB = new Date(b.created_at || b.createdAt || 0);
            return dateB - dateA; // Descending order
        });
        
        // Check which tasks have open tabs
        const openTaskIds = this.openTabs
            .filter(t => t.type === 'task')
            .map(t => t.taskId);
        
        // Show first 3, rest in collapsed section
        const visibleItems = sortedTasks.slice(0, 3);
        const collapsedItems = sortedTasks.slice(3);
        
        const renderTaskItem = (item) => {
            const isOpen = openTaskIds.includes(item.id);
            const isActive = item.id === this.currentTaskId;
            const isFailed = item.status?.toLowerCase() === 'failed';
            const displayName = this._getItemDisplayName(item, 'Task');
            const statusClass = this._getTaskStatusClass(item.status);
            return `
                <div class="a2ui-list-item ${isActive ? 'active' : ''} ${isOpen ? 'has-tab' : ''}" 
                     data-task-id="${item.id}" data-item-type="task">
                    <i data-lucide="list-checks"></i>
                    <span class="a2ui-item-text">${escapeHtml(displayName)}</span>
                    <span class="a2ui-item-status ${statusClass} ${isFailed ? 'status-failed' : ''}">${item.status || 'unknown'}</span>
                </div>
            `;
        };
        
        let html = visibleItems.map(renderTaskItem).join('');
        
        // Add collapsed section for remaining items
        if (collapsedItems.length > 0) {
            const isExpanded = this._expandedSections?.tasksList || false;
            html += `
                <div class="a2ui-collapsible-list ${isExpanded ? 'expanded' : ''}">
                    <div class="a2ui-show-more-toggle" data-toggle-list="tasksList">
                        <i data-lucide="${isExpanded ? 'chevron-up' : 'chevron-down'}"></i>
                        <span>${isExpanded ? 'Hide' : 'Show'} ${collapsedItems.length} more task${collapsedItems.length > 1 ? 's' : ''}</span>
                    </div>
                    <div class="a2ui-collapsible-content">
                        ${collapsedItems.map(renderTaskItem).join('')}
                    </div>
                </div>
            `;
        }
        
        safeSetInnerHTML(container, html);
        
        // Add click handlers for tasks
        container.querySelectorAll('.a2ui-list-item').forEach(item => {
            item.addEventListener('click', () => this._selectTask(item.dataset.taskId));
        });
        
        // Add toggle handler for show more
        const toggleBtn = container.querySelector('.a2ui-show-more-toggle');
        if (toggleBtn) {
            toggleBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this._toggleListExpansion('tasksList');
            });
        }
        
        this._refreshIcons();
    }
    
    /**
     * Get CSS class for task status display
     */
    _getTaskStatusClass(status) {
        if (!status) return '';
        const normalized = status.replace(/_/g, '-').toLowerCase();
        switch (normalized) {
            case 'completed': return 'status-completed';
            case 'failed': return 'status-failed';
            case 'working': 
            case 'in-progress': return 'status-working';
            case 'submitted': return 'status-submitted';
            case 'cancelled': return 'status-cancelled';
            default: return '';
        }
    }
    
    /**
     * Generate a display name for an item based on its content
     * Priority: title > keywords from first message > fallback
     */
    _getItemDisplayName(item, fallback = 'Item') {
        // Use title if available and not generic
        if (item.title && item.title !== fallback && item.title.trim().length > 0) {
            return item.title.length > 25 ? item.title.substring(0, 25) + '...' : item.title;
        }
        
        // Try to extract keywords from context or first message
        if (item.messages && item.messages.length > 0) {
            const firstMessage = item.messages[0];
            const keywords = firstMessage?.metadata?.keywords || [];
            
            if (keywords.length > 0) {
                // Create pipe-separated label from keywords
                const labels = keywords.map(k => k.label).filter(Boolean);
                if (labels.length > 0) {
                    const combined = labels.join(' | ');
                    return combined.length > 25 ? combined.substring(0, 25) + '...' : combined;
                }
            }
            
            // Fallback: use first few words of content
            const content = Array.isArray(firstMessage?.content)
                ? firstMessage.content.find(p => p.type === 'text')?.text
                : firstMessage?.content;
            
            if (content && typeof content === 'string') {
                const words = content.trim().split(/\s+/).slice(0, 4).join(' ');
                return words.length > 25 ? words.substring(0, 25) + '...' : words;
            }
        }
        
        return fallback;
    }
    
    _renderArtifacts() {
        const container = this.container.querySelector('#a2ui-artifacts-list');
        const badge = this.container.querySelector('#a2ui-artifacts-count');
        
        if (badge) badge.textContent = this.artifacts.length;
        
        if (!container) return;
        
        if (this.artifacts.length === 0) {
            safeSetInnerHTML(container, '<div class="a2ui-empty-section">No artifacts yet</div>');
            return;
        }
        
        safeSetInnerHTML(container, this.artifacts.slice(0, 5).map(artifact => {
            // Determine artifact type icon
            let icon = 'file-box';
            let typeLabel = artifact.type || 'file';
            
            if (artifact.type === 'mcp_ui' || artifact.ui_resource) {
                icon = 'layout';
                typeLabel = 'MCP UI';
            } else if (artifact.type === 'a2ui' || artifact.payload) {
                icon = 'component';
                typeLabel = 'A2UI';
            } else if (artifact.type === 'code') {
                icon = 'code';
            } else if (artifact.type === 'image') {
                icon = 'image';
            } else if (artifact.type === 'data') {
                icon = 'database';
            }
            
            return `
                <div class="a2ui-list-item a2ui-artifact-item" 
                     data-artifact-id="${artifact.id}"
                     data-artifact-type="${artifact.type || 'text'}">
                    <i data-lucide="${icon}"></i>
                    <span class="a2ui-item-text">${escapeHtml(artifact.name || artifact.title || typeLabel)}</span>
                    <span class="a2ui-artifact-type-badge">${escapeHtml(typeLabel)}</span>
                </div>
            `;
        }).join(''));
        
        // Add click handlers for artifact preview
        container.querySelectorAll('.a2ui-artifact-item').forEach(item => {
            item.addEventListener('click', () => this._showArtifactPreview(item.dataset.artifactId));
        });
        
        this._refreshIcons();
    }
    
    /**
     * Show artifact preview modal/panel
     * @param {string} artifactId - Artifact ID to preview
     */
    _showArtifactPreview(artifactId) {
        const artifact = this.artifacts.find(a => a.id === artifactId);
        if (!artifact) return;
        
        // Create or get preview container
        let previewEl = this.container.querySelector('.a2ui-artifact-preview');
        if (!previewEl) {
            previewEl = document.createElement('div');
            previewEl.className = 'a2ui-artifact-preview';
            this.container.querySelector('.a2ui-canvas').appendChild(previewEl);
        }
        
        // Render artifact content based on type
        let contentHtml = '';
        
        if (artifact.type === 'mcp_ui' || artifact.ui_resource) {
            // MCP UI artifact
            contentHtml = renderMCPUIArtifact(artifact, (action) => this._handleMCPUIAction(action));
        } else if (artifact.type === 'a2ui' || artifact.payload) {
            // A2UI artifact
            contentHtml = renderA2UIArtifact(artifact);
        } else if (artifact.type === 'code') {
            // Code artifact
            contentHtml = `<pre class="a2ui-preview-code" data-language="${escapeHtml(artifact.language || '')}"><code>${escapeHtml(artifact.content || '')}</code></pre>`;
        } else if (artifact.type === 'image') {
            // Image artifact
            const src = artifact.url || (artifact.base64 ? `data:${artifact.mime_type || 'image/png'};base64,${artifact.base64}` : '');
            contentHtml = `<img class="a2ui-preview-image" src="${escapeHtml(src)}" alt="${escapeHtml(artifact.name || '')}" />`;
        } else if (artifact.type === 'data') {
            // Data artifact - show JSON
            contentHtml = `<pre class="a2ui-preview-data"><code>${escapeHtml(JSON.stringify(artifact.data, null, 2))}</code></pre>`;
        } else {
            // Text/default artifact
            contentHtml = `<div class="a2ui-preview-text">${escapeHtml(artifact.content || '')}</div>`;
        }
        
        safeSetInnerHTML(previewEl, `
            <div class="a2ui-preview-header">
                <span class="a2ui-preview-title">${escapeHtml(artifact.name || artifact.title || 'Artifact')}</span>
                <button class="a2ui-preview-close" title="Close">
                    <i data-lucide="x"></i>
                </button>
            </div>
            <div class="a2ui-preview-content">
                ${contentHtml}
            </div>
        `);
        
        previewEl.classList.add('visible');
        
        // Close button handler
        previewEl.querySelector('.a2ui-preview-close').addEventListener('click', () => {
            previewEl.classList.remove('visible');
        });
        
        // Setup A2UI event handlers if applicable
        if (artifact.type === 'a2ui' || artifact.payload) {
            this._setupA2UIEventHandlers(previewEl, artifact);
        }
        
        // Setup MCP UI message handlers if applicable
        if (artifact.type === 'mcp_ui' || artifact.ui_resource) {
            this._setupMCPUIMessageHandlers(previewEl, artifact);
        }
        
        this._refreshIcons();
    }
    
    /**
     * Handle MCP UI actions from iframe postMessage
     * @param {Object} action - Action payload
     */
    _handleMCPUIAction(action) {
        console.log('[A2UIChat] MCP UI Action:', action);
        
        switch (action.type) {
            case 'tool':
                // Trigger tool call via A2A
                if (action.payload?.toolName) {
                    this._sendMessage(`[Tool: ${action.payload.toolName}] ${JSON.stringify(action.payload.params || {})}`);
                }
                break;
            case 'prompt':
                // Send prompt to agent
                if (action.payload?.text) {
                    this._sendMessage(action.payload.text);
                }
                break;
            case 'link':
                // Open URL
                if (action.payload?.url) {
                    window.open(action.payload.url, '_blank');
                }
                break;
            case 'notify':
                // Show notification (could integrate with toast system)
                console.log('[A2UIChat] Notification:', action.payload?.message);
                break;
            default:
                console.warn('[A2UIChat] Unknown MCP UI action type:', action.type);
        }
    }
    
    /**
     * Setup MCP UI iframe message handlers
     * @param {HTMLElement} container - Container element
     * @param {Object} artifact - MCP UI artifact
     */
    _setupMCPUIMessageHandlers(container, artifact) {
        const iframe = container.querySelector('.a2ui-mcp-ui-frame');
        if (!iframe) return;
        
        // Listen for postMessage from iframe
        const messageHandler = (event) => {
            // Validate origin if needed (for security)
            // For now, accept messages from same origin or sandboxed iframes
            
            if (event.data && typeof event.data === 'object') {
                if (event.data.type && ['tool', 'prompt', 'intent', 'notify', 'link'].includes(event.data.type)) {
                    this._handleMCPUIAction(event.data);
                }
            }
        };
        
        window.addEventListener('message', messageHandler);
        
        // Store handler for cleanup
        iframe._mcpMessageHandler = messageHandler;
    }
    
    /**
     * Setup A2UI event handlers for interactive components
     * @param {HTMLElement} container - Container element  
     * @param {Object} artifact - A2UI artifact
     */
    _setupA2UIEventHandlers(container, artifact) {
        // Button click handlers
        container.querySelectorAll('.a2ui-comp-button[data-action]').forEach(btn => {
            btn.addEventListener('click', () => {
                const action = btn.dataset.action;
                if (action) {
                    this._handleA2UIAction(action, { componentId: btn.dataset.componentId }, artifact);
                }
            });
        });
        
        // Input change handlers
        container.querySelectorAll('.a2ui-field-input[data-action], .a2ui-select-input[data-action]').forEach(input => {
            input.addEventListener('change', () => {
                const action = input.dataset.action;
                if (action) {
                    this._handleA2UIAction(action, { 
                        componentId: input.closest('[data-component-id]')?.dataset.componentId,
                        value: input.value 
                    }, artifact);
                }
            });
        });
        
        // Chip click handlers
        container.querySelectorAll('.a2ui-comp-chip[data-action]').forEach(chip => {
            chip.addEventListener('click', () => {
                const action = chip.dataset.action;
                if (action) {
                    this._handleA2UIAction(action, { componentId: chip.dataset.componentId }, artifact);
                }
            });
        });
    }
    
    /**
     * Handle A2UI component actions
     * @param {string} action - Action identifier (e.g., "booking/submit")
     * @param {Object} data - Action data
     * @param {Object} artifact - Source artifact
     */
    _handleA2UIAction(action, data, artifact) {
        console.log('[A2UIChat] A2UI Action:', action, data);
        
        // Actions follow pattern: domain/action or just action
        const [domain, actionName] = action.includes('/') ? action.split('/') : ['default', action];
        
        // Send action as message to agent for processing
        this._sendMessage(`[A2UI Action: ${action}] ${JSON.stringify(data)}`);
    }
    
    _renderAgents() {
        const container = this.container.querySelector('#a2ui-agents-list');
        if (!container) return;
        
        // Show appropriate message based on state
        if (!this.options.workspaceId) {
            safeSetInnerHTML(container, '<div class="a2ui-empty-section">Select workspace in Context tab</div>');
            return;
        }
        
        if (this.agents.length === 0) {
            safeSetInnerHTML(container, '<div class="a2ui-empty-section">No agents available</div>');
            return;
        }
        
        // Find selected agent
        const selectedAgent = this.agents.find(a => a.id === this.selectedAgentId);
        const otherAgents = this.agents.filter(a => a.id !== this.selectedAgentId);
        
        let html = '';
        
        // Show selected agent first (always visible)
        if (selectedAgent) {
            html += `
                <div class="a2ui-selectable-item agent selected" data-agent-id="${selectedAgent.id}">
                    <div class="a2ui-item-icon">
                        <i data-lucide="bot"></i>
                    </div>
                    <div class="a2ui-item-info">
                        <span class="a2ui-item-name">${escapeHtml(selectedAgent.name)}</span>
                        <span class="a2ui-item-desc">${escapeHtml(selectedAgent.description || (selectedAgent.is_system ? 'System Agent' : 'Custom Agent'))}</span>
                    </div>
                    <i data-lucide="check" class="a2ui-check"></i>
                </div>
            `;
        }
        
        // Show other agents in a collapsed section
        if (otherAgents.length > 0) {
            const isExpanded = this._expandedSections?.agentsList || false;
            html += `
                <div class="a2ui-collapsible-list ${isExpanded ? 'expanded' : ''}">
                    <div class="a2ui-show-more-toggle" data-toggle-list="agentsList">
                        <i data-lucide="${isExpanded ? 'chevron-up' : 'chevron-down'}"></i>
                        <span>${isExpanded ? 'Hide' : 'Show'} ${otherAgents.length} more agent${otherAgents.length > 1 ? 's' : ''}</span>
                    </div>
                    <div class="a2ui-collapsible-content">
                        ${otherAgents.map(agent => `
                            <div class="a2ui-selectable-item agent" data-agent-id="${agent.id}">
                                <div class="a2ui-item-icon">
                                    <i data-lucide="bot"></i>
                                </div>
                                <div class="a2ui-item-info">
                                    <span class="a2ui-item-name">${escapeHtml(agent.name)}</span>
                                    <span class="a2ui-item-desc">${escapeHtml(agent.description || (agent.is_system ? 'System Agent' : 'Custom Agent'))}</span>
                                </div>
                            </div>
                        `).join('')}
                    </div>
                </div>
            `;
        }
        
        safeSetInnerHTML(container, html);
        
        // Add click handlers for agents
        container.querySelectorAll('.a2ui-selectable-item').forEach(item => {
            item.addEventListener('click', () => this._selectAgent(item.dataset.agentId));
        });
        
        // Add toggle handler for show more
        const toggleBtn = container.querySelector('.a2ui-show-more-toggle');
        if (toggleBtn) {
            toggleBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this._toggleListExpansion('agentsList');
            });
        }
        
        this._refreshIcons();
    }
    
    /**
     * Toggle expansion of a collapsible list (agents, tasks)
     */
    _toggleListExpansion(listName) {
        this._expandedSections[listName] = !this._expandedSections[listName];
        
        // Re-render the appropriate list
        if (listName === 'agentsList') {
            this._renderAgents();
        } else if (listName === 'tasksList') {
            this._renderTasks();
        }
    }
    
    /**
     * Toggle collapse state of a sidebar section
     */
    _toggleSection(sectionName) {
        const section = this.container.querySelector(`[data-section="${sectionName}"]`);
        if (!section) return;
        
        const isCollapsed = section.classList.toggle('collapsed');
        const icon = section.querySelector('.a2ui-collapse-icon');
        
        if (icon) {
            // Update chevron direction
            icon.setAttribute('data-lucide', isCollapsed ? 'chevron-right' : 'chevron-down');
            this._refreshIcons();
        }
    }
    
    /**
     * Setup collapse toggle handlers for sidebar sections
     */
    _setupSectionToggles() {
        if (!this.container) {
            console.warn('[A2UIChat] Container not available for _setupSectionToggles');
            return;
        }
        const headers = this.container.querySelectorAll('[data-toggle]');
        headers.forEach(header => {
            header.addEventListener('click', () => {
                const sectionName = header.getAttribute('data-toggle');
                this._toggleSection(sectionName);
            });
            // Add cursor pointer style
            header.style.cursor = 'pointer';
        });
    }
    
    _renderCursor() {
        const cursorNameEl = this.container.querySelector('#a2ui-cursor-name');
        if (cursorNameEl) {
            // Show 'Me' for the default cursor, otherwise show the cursor name or ID
            const displayName = this.cursorName || (this.cursorId === 'me' ? 'Me' : this.cursorId) || 'Me';
            cursorNameEl.textContent = displayName;
            cursorNameEl.title = this.cursorId || '';
        }
    }
    
    /**
     * Load cursor graph stats (node and edge counts)
     */
    async _loadCursorStats() {
        const statsEl = this.container.querySelector('#a2ui-cursor-stats');
        if (!statsEl) return;
        
        // Allow empty string apiEndpoint (for proxied dev environment)
        if (!this.options.workspaceId || this.options.apiEndpoint === null || this.options.apiEndpoint === undefined) {
            statsEl.textContent = '0 nodes, 0 edges';
            return;
        }
        
        const cursorId = this.cursorId || 'me';
        
        try {
            const graphData = await this._apiRequest(
                `/workspaces/${this.options.workspaceId}/cursors/${cursorId}/graph/data`
            );
            
            const nodeCount = graphData?.meta?.nodeCount ?? graphData?.nodes?.length ?? 0;
            const edgeCount = graphData?.meta?.edgeCount ?? graphData?.edges?.length ?? 0;
            
            statsEl.textContent = `${nodeCount} nodes, ${edgeCount} edges`;
        } catch (err) {
            console.warn('[A2UIChat] Failed to load cursor stats:', err);
            statsEl.textContent = '0 nodes, 0 edges';
        }
    }
    
    /**
     * Load default cursor (/cursors/me)
     */
    async _loadDefaultCursor() {
        // Always use 'me' cursor - it's the current user's cursor
        // Don't override if already loaded
        if (this.cursorId && this.cursorName) {
            this._renderCursor();
            return;
        }
        
        // Allow empty string apiEndpoint (for proxied dev environment)
        if (!this.options.workspaceId || this.options.apiEndpoint === null || this.options.apiEndpoint === undefined) return;
        
        try {
            const cursor = await this._apiRequest(
                `/workspaces/${this.options.workspaceId}/cursors/me`
            );
            
            if (cursor && cursor.id) {
                this.selectedCursorId = cursor.id;
                this.cursorId = cursor.id;
                this.cursorName = cursor.name || 'Me';
                await this._saveState();
                this._renderCursor();
                this._loadCursorStats();
                console.log('[A2UIChat] Loaded default cursor:', cursor.id, cursor.name);
            }
        } catch (err) {
            console.warn('[A2UIChat] Failed to load default cursor:', err);
        }
    }
    
    _renderMessages() {
        const messagesEl = this.container.querySelector('#a2ui-messages');
        if (!messagesEl) return;
        
        const { layoutMode } = this.options;
        const isEmpty = this.messages.length === 0 && !this.isStreaming;
        
        // Normalize parts→content and pair tool_call+tool_response
        const prepared = this._pairToolMessages(this.messages);
        
        // Render each message with appropriate component
        const messagesHtml = prepared.map(m => {
            if (m.role === 'error') {
                // Use error bubble for error messages
                const errorText = Array.isArray(m.content)
                    ? m.content.filter(p => p.type === 'text').map(p => p.text).join('\n')
                    : (typeof m.content === 'string' ? m.content : 'Unknown error');
                return createErrorBubble(errorText, m.metadata?.agent_name || 'Agent', m.timestamp, layoutMode);
            }
            return createMessageBubble(m, layoutMode);
        }).join('');
        
        safeSetInnerHTML(messagesEl, `
            ${isEmpty ? this._getEmptyState() : ''}
            ${messagesHtml}
            ${this.isStreaming ? createStreamingIndicator(layoutMode) : ''}
        `);
        
        // Toggle bottom input visibility (JS-controlled; more reliable than CSS :has())
        const chatInput = this.container.querySelector('.a2ui-chat-input');
        if (isEmpty) {
            if (chatInput) chatInput.style.display = 'none';
            messagesEl.classList.add('is-empty');
        } else {
            if (chatInput) chatInput.style.display = '';
            messagesEl.classList.remove('is-empty');
            messagesEl.scrollTop = messagesEl.scrollHeight;
        }

        // Setup empty state input if showing empty state
        if (isEmpty) {
            this._setupEmptyStateInput();
        }
        
        this._refreshIcons();
    }
    
    /**
     * Setup the entity input in the empty state
     */
    _setupEmptyStateInput() {
        const emptyInputContainer = this.container.querySelector('#a2ui-empty-entity-input-container');
        const emptySendBtn = this.container.querySelector('#a2ui-empty-send');
        
        if (!emptyInputContainer) return;
        
        // Try to setup EntityInput, fall back to simple textarea
        try {
            if (EntityInputGlobal) {
                // Use same initialization pattern as the main input
                this.emptyEntityInput = new EntityInputGlobal({
                    placeholder: 'Type to search or ask a question...',
                    debounceMs: 300,
                    minSearchLength: 2,
                    theme: this.options.theme,
                    dropdownPosition: 'below',
                    onSearch: (query) => this._handleSuggestionInput(query),
                    onSubmit: (data) => this._handleSendFromEntityInput(data),
                    onEntitySelect: (entity) => {
                        console.log('[A2UIChat] Entity selected (empty state):', entity);
                    },
                    onChange: (data) => {
                        // Could be used for draft saving
                    },
                });
                this.emptyEntityInput.init(emptyInputContainer);
            } else {
                // Fallback to simple input
                safeSetInnerHTML(emptyInputContainer, `
                    <textarea 
                        id="a2ui-empty-simple-input" 
                        class="a2ui-simple-textarea"
                        placeholder="Type to search or ask a question..."
                        rows="1"
                    ></textarea>
                `);
                
                const textarea = emptyInputContainer.querySelector('#a2ui-empty-simple-input');
                if (textarea) {
                    textarea.addEventListener('input', () => this._autoResize(textarea));
                    textarea.addEventListener('keydown', (e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            const content = textarea.value.trim();
                            if (content) {
                                this._handleSendFromEntityInput({ 
                                    query: content, 
                                    entities: [],
                                    keywords: []
                                });
                            }
                        }
                    });
                }
            }
        } catch (err) {
            console.warn('[A2UIChat] Failed to setup empty state input:', err);
        }
        
        // Setup send button
        if (emptySendBtn) {
            emptySendBtn.addEventListener('click', () => {
                const textarea = emptyInputContainer.querySelector('textarea');
                if (textarea) {
                    const content = textarea.value.trim();
                    if (content) {
                        this._handleSendFromEntityInput({ 
                            query: content, 
                            entities: [],
                            keywords: []
                        });
                    }
                }
            });
        }
    }
    
    // ========================================
    // SELECTION HANDLERS
    // ========================================

    async _selectAgent(agentId) {
        this.selectedAgentId = agentId;
        await this._saveState();
        this._renderAgents();
    }
    
    /**
     * Resolve agent display name from agent_id using loaded agents list
     * @param {string} agentId
     * @returns {string|null}
     */
    _resolveAgentName(agentId) {
        if (!agentId) return null;
        const agent = this.agents.find(a => a.id === agentId);
        return agent?.name || agent?.title || null;
    }
    
    /**
     * Resolve persona display name from persona_id.
     * Uses the current selectedPersonaName if persona matches, otherwise
     * falls back to looking up from stored persona data in tasks.
     * @param {string} personaId
     * @returns {string|null}
     */
    _resolvePersonaName(personaId) {
        if (!personaId) return null;
        // If it matches the currently selected persona, use its name
        if (personaId === this.selectedPersonaId && this.selectedPersonaName) {
            return this.selectedPersonaName;
        }
        // Try to find in tasks that reference this persona
        // (personas aren't always loaded as a separate list)
        return null;
    }
    
    async _selectTask(taskId) {
        // Find task in tasks list and generate display name
        const item = this.tasks.find(t => t.id === taskId);
        const itemName = item ? this._getItemDisplayName(item, 'Task') : 'Task';
        
        // Open task in a new tab (or switch to existing)
        this._openTaskTab(taskId, itemName);
    }
    
    /**
     * Open a task in a new tab or switch to existing tab
     */
    _openTaskTab(taskId, taskName) {
        // Check if tab already exists
        const existingTab = this.openTabs.find(t => t.taskId === taskId);
        
        if (existingTab) {
            // Switch to existing tab
            this._switchTab(existingTab.id);
            return;
        }
        
        // Check max tabs limit (excluding main)
        const taskTabs = this.openTabs.filter(t => t.type === 'task');
        if (taskTabs.length >= MAX_TABS - 1) {
            // Close oldest task tab (first task tab in array)
            const oldestTab = taskTabs[0];
            this._closeTab(oldestTab.id, false); // Don't switch, we're about to add new tab
        }
        
        // Create new tab
        const newTab = {
            id: `task-${taskId}`,
            type: 'task',
            name: taskName.length > 15 ? taskName.substring(0, 15) + '...' : taskName,
            taskId: taskId,
        };
        
        this.openTabs.push(newTab);
        
        // Switch to new tab
        this._switchTab(newTab.id);
    }
    
    /**
     * Open a task in a new tab WITHOUT loading messages.
     * Used during streaming: the tab is created and activated but messages stay
     * in this.messages (the stream will keep adding to them).
     */
    _openTaskTabNoLoad(taskId, taskName) {
        // Check if tab already exists — if so, just activate it visually
        const existingTab = this.openTabs.find(t => t.taskId === taskId);
        const tabId = existingTab ? existingTab.id : `task-${taskId}`;
        
        if (!existingTab) {
            // Check max tabs limit (excluding main)
            const taskTabs = this.openTabs.filter(t => t.type === 'task');
            if (taskTabs.length >= MAX_TABS - 1) {
                const oldestTab = taskTabs[0];
                this._closeTab(oldestTab.id, false);
            }
            
            const newTab = {
                id: tabId,
                type: 'task',
                name: taskName.length > 15 ? taskName.substring(0, 15) + '...' : taskName,
                taskId: taskId,
            };
            this.openTabs.push(newTab);
        }
        
        // Activate the tab visually (update state) but do NOT load messages
        this.activeTabId = tabId;
        this.currentTaskId = taskId;
        
        // Update agent selection to match task
        const taskItem = this.tasks.find(t => t.id === taskId);
        if (taskItem && taskItem.agent_id) {
            this.selectedAgentId = taskItem.agent_id;
            this._renderAgents();
        }
        
        this._saveState();
        this._renderTabBar();
        this._setupTabHandlers();
        this._renderTasks();
    }
    
    /**
     * Open an externally-created task (e.g. from ExplorerAPI.searchAsk).
     *
     * Unlike _openTaskTab (which loads history synchronously), this method
     * checks the task status and, if the task is still being processed
     * (submitted / working), shows the user message together with the
     * streaming "..." indicator and opens an SSE stream so the agent
     * response appears in real-time—exactly as it does when the user sends
     * a message directly through the chat input.
     *
     * @param {string} taskId   - A2A task ID
     * @param {string} taskName - Display name for the tab
     */
    async openExternalTask(taskId, taskName) {
        if (!taskId || !this.options.workspaceId) return;

        // Create / activate tab without loading messages yet
        this._openTaskTabNoLoad(taskId, taskName);

        // Retry fetching the task with back-off – newly-created tasks may
        // hit transient 502 / CORS errors while the backend is processing.
        const MAX_RETRIES = 5;
        const BASE_DELAY = 1500;
        let task = null;
        let lastErr = null;

        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
            try {
                task = await this._apiRequest(
                    `/workspaces/${this.options.workspaceId}/a2a/tasks/${taskId}`
                );
                break;  // success
            } catch (err) {
                lastErr = err;
                const isTransient = !err.status || err.status >= 500 || err.message?.includes('NetworkError');
                if (!isTransient || attempt === MAX_RETRIES - 1) {
                    throw err;  // non-retryable or exhausted retries
                }
                const delay = BASE_DELAY * Math.pow(1.5, attempt);
                console.warn(`[A2UIChat] openExternalTask retry ${attempt + 1}/${MAX_RETRIES} in ${Math.round(delay)}ms`, err.message || err);
                await new Promise(r => setTimeout(r, delay));
            }
        }

        try {
            const rawStatus = task?.status || task?.state || '';
            const status = rawStatus.replace(/_/g, '-').toLowerCase();
            const isRunning = ['submitted', 'working', 'in-progress'].includes(status);
            console.log('[A2UIChat] openExternalTask status:', rawStatus, '→', status, 'isRunning:', isRunning);

            // Load any existing messages (e.g. the user's initial message)
            const rawMessages = task?.history || task?.messages || [];
            const agentName = this._resolveAgentName(task.agent_id);
            const personaName = this._resolvePersonaName(task.persona_id);

            this.messages = rawMessages.map(msg => ({
                ...msg,
                metadata: {
                    ...msg.metadata,
                    ...(msg.role === 'user' && personaName ? { persona_name: personaName } : {}),
                    ...(msg.role !== 'user' && agentName ? { agent_name: agentName } : {}),
                },
            }));

            if (isRunning) {
                // Task is still being processed → show the streaming indicator
                // and start an SSE stream to capture the agent response.
                this.isStreaming = true;
                this._renderMessages();

                // Ensure the agent is selected (for the agent name in the response)
                if (task.agent_id) {
                    this.selectedAgentId = task.agent_id;
                    this._renderAgents();
                }

                // Start streaming from the task's /stream endpoint
                await this._streamExistingTask(taskId);
            } else {
                // Task is already complete (or failed) → render as-is
                this._renderMessages();
            }
        } catch (err) {
            console.error('[A2UIChat] openExternalTask failed:', err);
            // Fall back to regular history load — schedule a deferred retry
            // so the tab content loads once the backend recovers.
            await this._loadTaskHistory();
            if (!this.messages || this.messages.length === 0) {
                this._scheduleTaskHistoryRetry(taskId, taskName);
            }
        }
    }

    /**
     * Schedule deferred retries to load task history when the initial
     * openExternalTask and _loadTaskHistory both fail due to transient
     * errors (502, CORS, network). Retries with exponential back-off.
     */
    _scheduleTaskHistoryRetry(taskId, taskName, attempt = 0) {
        const MAX_RETRIES = 6;
        const BASE_DELAY = 2000;
        if (attempt >= MAX_RETRIES) {
            console.warn('[A2UIChat] Gave up retrying task history for', taskId);
            return;
        }
        const delay = BASE_DELAY * Math.pow(1.5, attempt);
        console.log(`[A2UIChat] Scheduling task history retry ${attempt + 1}/${MAX_RETRIES} in ${Math.round(delay)}ms`);
        setTimeout(async () => {
            // Only retry if the tab is still active for this task
            if (this.currentTaskId !== taskId) return;
            try {
                await this._loadTaskHistory();
                if (this.messages && this.messages.length > 0) {
                    console.log('[A2UIChat] Deferred task history loaded successfully');
                    return;  // success — stop retrying
                }
            } catch (_) { /* will retry */ }
            this._scheduleTaskHistoryRetry(taskId, taskName, attempt + 1);
        }, delay);
    }

    /**
     * Attach to an existing task's SSE stream endpoint and render the
     * agent response in real-time.  Mirrors _sendMessageStream but
     * skips the task-creation / message-posting steps.
     *
     * @param {string} taskId - A2A task ID to stream
     */
    async _streamExistingTask(taskId) {
        const streamUrl = `${this.options.apiEndpoint}/workspaces/${this.options.workspaceId}/a2a/tasks/${taskId}/stream`;
        const streamHeaders = {
            'Accept': 'text/event-stream',
            ...(this.options.accessToken && {
                'Authorization': `Bearer ${this.options.accessToken}`
            }),
        };

        try {
            const response = await fetch(streamUrl, {
                method: 'GET',
                headers: streamHeaders,
            });

            if (!response.ok) {
                // Stream not available (task may already be complete) – just reload
                console.warn('[A2UIChat] Stream unavailable for task', taskId, response.status);
                this.isStreaming = false;
                await this._loadTaskHistory();
                this._renderMessages();
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let agentResponse = '';
            let streamedContentParts = [];
            let taskError = null;
            let taskStatus = null;
            let agentMsgMetadata = null;
            let hasInjectedMessages = false;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    const data = line.slice(6);
                    if (data === '[DONE]') {
                        setTimeout(() => this._loadTasks(), 500);
                        continue;
                    }

                    try {
                        const event = JSON.parse(data);

                        if (event.error) {
                            taskError = event.error.message || event.error.data || 'Unknown error';
                            continue;
                        }

                        const result = event.result;
                        if (result) {
                            if (result.kind === 'status-update') {
                                const status = result.status;
                                const state = status?.state;
                                if (state) taskStatus = state;

                                if (result.final) {
                                    if (status.message && status.message.parts) {
                                        this._processStreamParts(status.message.parts, streamedContentParts);
                                        agentResponse += status.message.parts.map(p => p.text || p.content || '').join('');
                                    }
                                    if (state === 'failed') {
                                        taskError = status.error?.message || status.message || 'Task failed';
                                    }
                                    this._loadTasks();
                                } else if (status.message && status.message.parts) {
                                    this._processStreamParts(status.message.parts, streamedContentParts);
                                    agentResponse += status.message.parts.map(p => p.text || p.content || '').join('');
                                }
                                if (status?.error) {
                                    taskError = status.error.message || status.error || 'Task failed';
                                }
                                continue;
                            }

                            if (result.status?.state) taskStatus = result.status.state;

                            if (result.kind === 'message' && result.status?.message) {
                                const parts = result.status.message.parts || [];
                                this._processStreamParts(parts, streamedContentParts);
                                agentResponse = parts.filter(p => p.kind === 'text').map(p => p.text).join('\n');
                            }

                            if (result.kind === 'artifact' && result.artifact) {
                                if (!this.artifacts.find(a => a.id === result.artifact.id)) {
                                    this.artifacts.push(result.artifact);
                                    this._renderArtifacts();
                                }
                            }

                            if (result.final) break;
                        }

                        // Handle event_type / simple format
                        if (event.event_type === 'text' || event.event_type === 'content') {
                            agentResponse += event.text || event.content || '';
                        } else if (event.event_type === 'message' && event.message) {
                            const msgRole = event.message.role?.value || event.message.role || 'agent';
                            const msgParts = event.message.content || event.message.parts || [];
                            if (msgRole === 'agent' || msgRole === 'model') {
                                // Push each agent message individually so the
                                // rendering matches the history view (separate
                                // bubbles for tool calls and the final response).
                                if (event.message.metadata) agentMsgMetadata = { ...agentMsgMetadata, ...event.message.metadata };
                                const _agName = this.agents.find(a => a.id === this.selectedAgentId)?.name || 'Agent';
                                this.messages.push({
                                    role: 'agent',
                                    content: msgParts,
                                    timestamp: event.message.timestamp || new Date().toISOString(),
                                    metadata: { agent_id: this.selectedAgentId, agent_name: _agName, ...event.message.metadata },
                                });
                                hasInjectedMessages = true;
                                this._renderMessages();
                            }
                        } else if (event.event_type === 'task_status') {
                            taskStatus = event.status;
                            if (['completed', 'failed', 'cancelled'].includes(event.status)) {
                                if (event.status === 'failed') taskError = event.error || 'Task failed';
                                this._loadTasks();
                            }
                        } else if (event.event_type === 'error') {
                            taskError = event.error || 'Unknown error';
                            this._loadTasks();
                        }

                        if (event.type === 'text' || event.type === 'content') {
                            agentResponse += event.text || event.content || '';
                        } else if (event.delta) {
                            agentResponse += event.delta.content || event.delta.text || '';
                        }
                    } catch (parseErr) {
                        agentResponse += data;
                    }
                }
            }

            // Finalize: add agent response message
            const targetAgent = this.agents.find(a => a.id === this.selectedAgentId);
            const targetAgentName = targetAgent?.name || 'Agent';

            // --- explorer directive extraction (client-side fallback) ---
            let explorerAction = {};
            const _allTextParts2 = streamedContentParts.filter(p => p.type === 'text').map(p => p.text).join('');
            const _rawText2 = _allTextParts2 || agentResponse || '';
            const _directive2 = extractExplorerDirective(_rawText2);
            if (_directive2.action) {
                explorerAction = { explorer_action: _directive2.action };
                // Strip directive from content parts
                if (streamedContentParts.length > 0) {
                    const lastText = [...streamedContentParts].reverse().find(p => p.type === 'text');
                    if (lastText) lastText.text = lastText.text.replace(EXPLORER_DIRECTIVE_RE, '').trimEnd();
                } else if (agentResponse) {
                    agentResponse = _directive2.cleanText;
                }
            }
            const _baseMeta2 = { agent_id: this.selectedAgentId, agent_name: targetAgentName, ...agentMsgMetadata, ...explorerAction };

            if (taskError) {
                this.messages.push({
                    role: 'error',
                    content: [{ type: 'text', text: taskError }],
                    timestamp: new Date().toISOString(),
                    metadata: { ..._baseMeta2, task_status: taskStatus || 'failed', error: true },
                });
                await this._loadTasks();
            } else if (!hasInjectedMessages) {
                // Only add a synthetic message if no event_type=message events
                // were injected (i.e. JSON-RPC status-update path only)
                if (streamedContentParts.length > 0) {
                    this.messages.push({
                        role: 'agent',
                        content: streamedContentParts,
                        timestamp: new Date().toISOString(),
                        metadata: _baseMeta2,
                    });
                } else if (agentResponse) {
                    this.messages.push({
                        role: 'agent',
                        content: [{ type: 'text', text: agentResponse }],
                        timestamp: new Date().toISOString(),
                        metadata: _baseMeta2,
                    });
                }
            }
            // Notify parent that agent data may have changed
            if (hasInjectedMessages || streamedContentParts.length > 0 || agentResponse) {
                this._notifyDataChanged();
            }
        } catch (err) {
            console.error('[A2UIChat] _streamExistingTask error:', err);
            // Fall back to reloading task history
            await this._loadTaskHistory();
        } finally {
            this.isStreaming = false;
            this._renderMessages();
        }
    }
    
    /**
     * Switch to a tab
     */
    async _switchTab(tabId) {
        if (this.activeTabId === tabId) return;
        
        this.activeTabId = tabId;
        
        // Update current task ID based on tab
        const tab = this.openTabs.find(t => t.id === tabId);
        if (tab) {
            if (tab.type === 'main') {
                this.currentTaskId = null;
            } else {
                this.currentTaskId = tab.taskId;
                
                // Switch selected agent to match the task's agent
                const taskItem = this.tasks.find(t => t.id === tab.taskId);
                if (taskItem && taskItem.agent_id) {
                    this.selectedAgentId = taskItem.agent_id;
                    this._renderAgents();
                }
            }
        }
        
        // Save state and load appropriate messages
        await this._saveState();
        await this._loadTabMessages();
        
        // Re-render tab bar and lists
        this._renderTabBar();
        this._setupTabHandlers();
        this._renderTasks();
    }
    
    /**
     * Close a tab
     */
    _closeTab(tabId, switchToMain = true) {
        // Can't close main tab
        if (tabId === 'main') return;
        
        const tabIndex = this.openTabs.findIndex(t => t.id === tabId);
        if (tabIndex === -1) return;
        
        // Remove tab
        this.openTabs.splice(tabIndex, 1);
        
        // If closing active tab, switch to main
        if (this.activeTabId === tabId && switchToMain) {
            this._switchTab('main');
        } else {
            // Just re-render tab bar
            this._renderTabBar();
            this._setupTabHandlers();
        }
    }
    
    /**
     * Render the tab bar
     */
    _renderTabBar() {
        const tabsHtml = this.openTabs.map(tab => {
            const isActive = tab.id === this.activeTabId;
            const isMain = tab.type === 'main';
            
            return `
                <div class="a2ui-tab ${isActive ? 'active' : ''}" data-tab-id="${tab.id}">
                    <i data-lucide="${isMain ? 'plus' : 'list-checks'}"></i>
                    <span class="a2ui-tab-name">${escapeHtml(tab.name)}</span>
                    ${!isMain ? `<button class="a2ui-tab-close" data-tab-id="${tab.id}" title="Close tab">&times;</button>` : ''}
                </div>
            `;
        }).join('');
        
        // Update tab bar if container exists
        const tabBar = this.container?.querySelector('#a2ui-tab-bar');
        if (tabBar) {
            safeSetInnerHTML(tabBar, tabsHtml);
            this._refreshIcons();
        }
        
        return tabsHtml;
    }
    
    /**
     * Setup tab event handlers
     */
    _setupTabHandlers() {
        const tabBar = this.container?.querySelector('#a2ui-tab-bar');
        if (!tabBar) return;
        
        // Tab click handlers
        tabBar.querySelectorAll('.a2ui-tab').forEach(tab => {
            tab.addEventListener('click', (e) => {
                // Don't switch if clicking close button
                if (e.target.classList.contains('a2ui-tab-close')) return;
                this._switchTab(tab.dataset.tabId);
            });
        });
        
        // Close button handlers
        tabBar.querySelectorAll('.a2ui-tab-close').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                this._closeTab(btn.dataset.tabId);
            });
        });
    }
    
    /**
     * Load messages for current tab
     */
    async _loadTabMessages() {
        if (this.activeTabId === 'main') {
            // "New task" tab: show empty state with centered input
            this.messages = [];
            this._renderMessages();
        } else {
            // Task tab: load specific task history
            await this._loadTaskHistory();
        }
    }
    
    /**
     * Load all tasks' message history for Main tab
     */
    async _loadAllTasksHistory() {
        // Allow empty string apiEndpoint (for proxied dev environment)
        if (!this.options.workspaceId || this.options.apiEndpoint === null || this.options.apiEndpoint === undefined) {
            this.messages = [];
            this._renderMessages();
            return;
        }
        
        try {
            // Fetch all tasks with their messages
            const allMessages = [];
            
            // Use tasks list, limit to recent items to avoid loading too much
            const recentItems = this.tasks.slice(0, 10);
            
            for (const item of recentItems) {
                try {
                    const taskData = await this._apiRequest(
                        `/workspaces/${this.options.workspaceId}/a2a/tasks/${item.id}`
                    );
                    // Match explorer-tasks: response?.history || response?.messages
                    const taskMessages = taskData?.history || taskData?.messages || [];
                    if (taskMessages.length > 0) {
                        // Resolve agent/persona names from task metadata
                        const agentName = this._resolveAgentName(taskData.agent_id || item.agent_id);
                        const personaName = this._resolvePersonaName(taskData.persona_id || item.persona_id);
                        
                        // Add task context + names to each message
                        taskMessages.forEach(msg => {
                            allMessages.push({
                                ...msg,
                                metadata: {
                                    ...msg.metadata,
                                    task_id: item.id,
                                    task_title: item.title,
                                    ...(msg.role === 'user' && personaName ? { persona_name: personaName } : {}),
                                    ...(msg.role !== 'user' && agentName ? { agent_name: agentName } : {}),
                                },
                            });
                        });
                    }
                } catch (err) {
                    console.warn(`[A2UIChat] Failed to load task ${item.id}:`, err);
                }
            }
            
            // Sort by timestamp
            allMessages.sort((a, b) => {
                const timeA = new Date(a.timestamp || 0).getTime();
                const timeB = new Date(b.timestamp || 0).getTime();
                return timeA - timeB;
            });
            
            this.messages = allMessages;
            this._renderMessages();
        } catch (err) {
            console.error('[A2UIChat] Failed to load all tasks history:', err);
            this.messages = [];
            this._renderMessages();
        }
    }
    
    // ========================================
    // MESSAGE HANDLING
    // ========================================
    
    /**
     * Handle send from EntityInput callback
     */
    async _handleSendFromEntityInput(data) {
        const { query, keywords } = data;
        await this._sendMessage(query, keywords);
    }

    /**
     * Handle send message (from button click)
     */
    async _handleSend() {
        if (!this.entityInput) return;
        
        const message = this.entityInput.getPlainText();
        const keywords = this.entityInput.getSelectedEntities();
        
        await this._sendMessage(message, keywords);
    }
    
    /**
     * Send a message with optional keywords (entities)
     */
    async _sendMessage(message, keywords = []) {
        const plainText = message || '';
        
        // Build the display/send query by replacing entity label occurrences
        // inline with markdown entity links [Label](type:id).
        // If a label isn't found in the text, append it at the end.
        let fullQuery = plainText;
        const unreplaced = [];
        for (const e of keywords) {
            const entityLink = `[${e.label}](${e.type}:${e.id})`;
            // Replace the first occurrence of the label in text (case-insensitive)
            const idx = fullQuery.toLowerCase().indexOf(e.label.toLowerCase());
            if (idx !== -1) {
                fullQuery = fullQuery.substring(0, idx) + entityLink + fullQuery.substring(idx + e.label.length);
            } else {
                unreplaced.push(entityLink);
            }
        }
        // Append any entities whose labels weren't found in the text
        if (unreplaced.length > 0) {
            fullQuery += ' ' + unreplaced.join(' ');
        }
        
        if (!fullQuery.trim() || !this.selectedAgentId || this.isStreaming) {
            if (!this.selectedAgentId) {
                console.warn('[A2UIChat] No agent selected');
            }
            return;
        }
        
        // Clear input
        if (this.entityInput) {
            this.entityInput.clear();
        }
        
        // Add user message to UI with plain text + entity chips
        const userMessage = {
            role: 'user',
            content: [{ type: 'text', text: fullQuery }],
            timestamp: new Date().toISOString(),
            metadata: {
                query: plainText,
                keywords: keywords,
                persona_id: this.selectedPersonaId,
                persona_name: this.selectedPersonaName || 'You',
            },
        };
        this.messages.push(userMessage);
        this._renderMessages();
        
        // Callback
        if (this.options.onMessageSent) {
            this.options.onMessageSent(userMessage);
        }
        
        // Send to API with streaming
        await this._sendMessageStream(fullQuery);
    }

    /**
     * Send message with SSE streaming
     * Aligned with explorer-tasks API pattern:
     * - New tasks: POST /a2a/tasks (creates task), then GET /a2a/tasks/{id}/stream
     * - Existing tasks: POST /a2a/tasks/{id}/messages, then GET /a2a/tasks/{id}/stream
     */
    async _sendMessageStream(message) {
        // Allow empty string apiEndpoint (for proxied dev environment)
        if (!this.options.workspaceId || this.options.apiEndpoint === null || this.options.apiEndpoint === undefined) {
            console.error('[A2UIChat] Missing workspace or endpoint');
            return;
        }
        
        // Determine if we should continue the current task or start a new one
        let effectiveTaskId = this.currentTaskId;
        if (effectiveTaskId && this.selectedAgentId) {
            // Find the current task and check if agent matches
            const currentItem = this.tasks.find(t => t.id === effectiveTaskId);
            if (currentItem && currentItem.agent_id !== this.selectedAgentId) {
                console.log('[A2UIChat] Agent changed - starting new task instead of continuing', 
                    { taskAgent: currentItem.agent_id, selectedAgent: this.selectedAgentId });
                effectiveTaskId = null;  // Don't pass taskId, will create new task
            }
        }
        
        this.isStreaming = true;
        this._renderMessages();
        
        try {
            // Step 1: Create new task or send message to existing task
            let streamTaskId = effectiveTaskId;
            
            if (!effectiveTaskId) {
                // Create new task (matches explorer-tasks: POST /a2a/tasks)
                const createPayload = {
                    agent_id: this.selectedAgentId,
                    initial_message: {
                        role: 'user',
                        content: [{ type: 'text', text: message }]
                    },
                    metadata: {
                        persona_id: this.selectedPersonaId,
                        persona_name: this.selectedPersonaName || 'You',
                        cursor_id: this.selectedCursorId,
                        ...(this.options.canvasId && { canvas_id: this.options.canvasId }),
                        ...(this.selectedCanvasId && { canvas_id: this.selectedCanvasId }),
                    }
                };
                
                const createResponse = await this._apiRequest(
                    `/workspaces/${this.options.workspaceId}/a2a/tasks`,
                    {
                        method: 'POST',
                        body: JSON.stringify(createPayload),
                    }
                );
                
                streamTaskId = createResponse.id || createResponse.task_id;
                this.currentTaskId = streamTaskId;
                await this._saveState();
                
                // Refresh tasks list
                await this._loadTasks();
                
                // Create a tab for the new task WITHOUT loading messages
                // (streaming is about to start, so this.messages already has the user message
                // and the stream will add the agent response)
                const taskItem = this.tasks.find(t => t.id === streamTaskId);
                const taskName = taskItem ? this._getItemDisplayName(taskItem, 'Task') : 'Task';
                this._openTaskTabNoLoad(streamTaskId, taskName);
                
                if (this.options.onTaskCreated) {
                    this.options.onTaskCreated(streamTaskId);
                }
            } else {
                // Send message to existing task (matches explorer-tasks: POST /a2a/tasks/{id}/messages)
                const messagePayload = {
                    message: {
                        role: 'user',
                        content: [{ type: 'text', text: message }]
                    },
                    stream: true,
                    context: {
                        persona_id: this.selectedPersonaId,
                        persona_name: this.selectedPersonaName || 'You',
                    }
                };
                
                try {
                    await this._apiRequest(
                        `/workspaces/${this.options.workspaceId}/a2a/tasks/${effectiveTaskId}/messages`,
                        {
                            method: 'POST',
                            body: JSON.stringify(messagePayload),
                        }
                    );
                } catch (msgErr) {
                    if (msgErr.status === 404) {
                        // Task no longer exists (deleted or wrong workspace) — create a new one instead
                        console.warn('[A2UIChat] Task not found (404), creating new task instead');
                        this.currentTaskId = null;
                        await this._saveState();
                        
                        const createPayload = {
                            agent_id: this.selectedAgentId,
                            initial_message: {
                                role: 'user',
                                content: [{ type: 'text', text: message }]
                            },
                            metadata: {
                                persona_id: this.selectedPersonaId,
                                persona_name: this.selectedPersonaName || 'You',
                                cursor_id: this.selectedCursorId,
                                ...(this.options.canvasId && { canvas_id: this.options.canvasId }),
                                ...(this.selectedCanvasId && { canvas_id: this.selectedCanvasId }),
                            }
                        };
                        
                        const createResponse = await this._apiRequest(
                            `/workspaces/${this.options.workspaceId}/a2a/tasks`,
                            {
                                method: 'POST',
                                body: JSON.stringify(createPayload),
                            }
                        );
                        
                        streamTaskId = createResponse.id || createResponse.task_id;
                        this.currentTaskId = streamTaskId;
                        await this._saveState();
                        await this._loadTasks();
                        
                        const taskItem = this.tasks.find(t => t.id === streamTaskId);
                        const taskName = taskItem ? this._getItemDisplayName(taskItem, 'Task') : 'Task';
                        this._openTaskTabNoLoad(streamTaskId, taskName);
                        
                        if (this.options.onTaskCreated) {
                            this.options.onTaskCreated(streamTaskId);
                        }
                    } else {
                        throw msgErr;
                    }
                }
            }
            
            // Step 2: Stream response (matches explorer-tasks: GET /a2a/tasks/{id}/stream)
            const streamUrl = `${this.options.apiEndpoint}/workspaces/${this.options.workspaceId}/a2a/tasks/${streamTaskId}/stream`;
            const streamHeaders = {
                'Accept': 'text/event-stream',
                ...(this.options.accessToken && {
                    'Authorization': `Bearer ${this.options.accessToken}`
                }),
            };
            
            const response = await fetch(streamUrl, {
                method: 'GET',
                headers: streamHeaders,
            });
            
            if (!response.ok) {
                throw new Error(`Stream failed: HTTP ${response.status}: ${response.statusText}`);
            }
            
            // Handle SSE stream (aligned with explorer-tasks handleStreamEventForWindow)
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let agentResponse = '';
            let streamedContentParts = [];  // Rich content parts (text, mcp_ui, a2ui)
            let taskError = null;
            let taskStatus = null;
            let agentMsgMetadata = null;    // Metadata from the last agent SSE message event
            let hasInjectedMessages = false; // Whether event_type=message events were pushed directly
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                buffer += decoder.decode(value, { stream: true });
                
                // Parse SSE events
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';
                
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6);
                        if (data === '[DONE]') {
                            // Streaming complete - refresh tasks list
                            setTimeout(() => this._loadTasks(), 500);
                            continue;
                        }
                        
                        try {
                            const event = JSON.parse(data);
                            
                            // Check for JSON-RPC error
                            if (event.error) {
                                taskError = event.error.message || event.error.data || 'Unknown error';
                                console.error('[A2UIChat] JSON-RPC error:', event.error);
                                continue;
                            }
                            
                            const result = event.result;
                            
                            if (result) {
                                // A2A Protocol: status-update events (matches explorer-tasks)
                                if (result.kind === 'status-update') {
                                    const status = result.status;
                                    const state = status?.state;
                                    
                                    // Track task status
                                    if (state) taskStatus = state;
                                    
                                    // Handle final states
                                    if (result.final) {
                                        if (state === 'completed' || state === 'failed' || state === 'cancelled') {
                                            // Extract message from status.message.parts
                                            if (status.message && status.message.parts) {
                                                const parts = status.message.parts;
                                                this._processStreamParts(parts, streamedContentParts);
                                                agentResponse += parts.map(p => p.text || p.content || '').join('');
                                            }
                                            if (state === 'failed') {
                                                taskError = status.error?.message || status.message || 'Task failed';
                                            }
                                            // Refresh tasks list
                                            this._loadTasks();
                                        }
                                    } else if (status.message && status.message.parts) {
                                        // Streaming message parts
                                        const parts = status.message.parts;
                                        this._processStreamParts(parts, streamedContentParts);
                                        agentResponse += parts.map(p => p.text || p.content || '').join('');
                                    }
                                    
                                    // Check for error in status
                                    if (status?.error) {
                                        taskError = status.error.message || status.error || 'Task failed';
                                    }
                                    continue;
                                }
                                
                                // Track task ID (for JSON-RPC envelope)
                                if (result.taskId && !this.currentTaskId) {
                                    this.currentTaskId = result.taskId;
                                    await this._saveState();
                                    await this._loadTasks();
                                    
                                    if (this.options.onTaskCreated) {
                                        this.options.onTaskCreated(result.taskId);
                                    }
                                }
                                
                                // Track task status (for backward compat)
                                if (result.status?.state) {
                                    taskStatus = result.status.state;
                                }
                                
                                // Handle message content (text, mcp_ui, a2ui)
                                if (result.kind === 'message' && result.status?.message) {
                                    const msg = result.status.message;
                                    const parts = msg.parts || [];
                                    this._processStreamParts(parts, streamedContentParts);
                                    agentResponse = parts
                                        .filter(p => p.kind === 'text')
                                        .map(p => p.text)
                                        .join('\n');
                                }
                                
                                // Handle artifact events (MCP UI / A2UI artifacts)
                                if (result.kind === 'artifact' && result.artifact) {
                                    const artifact = result.artifact;
                                    if (!this.artifacts.find(a => a.id === artifact.id)) {
                                        this.artifacts.push(artifact);
                                        this._renderArtifacts();
                                    }
                                }
                                
                                // Check if final
                                if (result.final) {
                                    break;
                                }
                            }
                            
                            // Handle event_type format (from /stream endpoint - matches explorer-tasks)
                            if (event.event_type === 'text' || event.event_type === 'content') {
                                agentResponse += event.text || event.content || '';
                            } else if (event.event_type === 'message' && event.message) {
                                const msg = event.message;
                                const msgRole = msg.role?.value || msg.role || 'agent';
                                const msgParts = msg.content || msg.parts || [];
                                
                                if (msgRole === 'agent' || msgRole === 'model') {
                                    // Push each agent message individually so the
                                    // rendering matches the history view (separate
                                    // bubbles for tool calls and the final response).
                                    if (msg.metadata) agentMsgMetadata = { ...agentMsgMetadata, ...msg.metadata };
                                    const _agName = this.agents.find(a => a.id === this.selectedAgentId)?.name || 'Agent';
                                    this.messages.push({
                                        role: 'agent',
                                        content: msgParts,
                                        timestamp: msg.timestamp || new Date().toISOString(),
                                        metadata: { agent_id: this.selectedAgentId, agent_name: _agName, ...msg.metadata },
                                    });
                                    hasInjectedMessages = true;
                                    this._renderMessages();
                                    console.log('[A2UIChat] Injected agent message, total messages:', this.messages.length);
                                }
                                // Skip user messages (already in this.messages from _sendMessage)
                            } else if (event.event_type === 'task_status') {
                                console.log('[A2UIChat] Task status event:', event.status);
                                taskStatus = event.status;
                                if (event.status === 'completed' || event.status === 'failed' || event.status === 'cancelled') {
                                    if (event.status === 'failed') {
                                        taskError = event.error || 'Task failed';
                                    }
                                    this._loadTasks();
                                }
                            } else if (event.event_type === 'error') {
                                taskError = event.error || 'Unknown error';
                                this._loadTasks();
                            }
                            
                            // Simple type field format / OpenAI-style delta
                            if (event.type === 'text' || event.type === 'content') {
                                agentResponse += event.text || event.content || '';
                            } else if (event.delta) {
                                agentResponse += event.delta.content || event.delta.text || '';
                            }
                            
                        } catch (parseErr) {
                            // Non-JSON data, might be raw text
                            agentResponse += data;
                        }
                    }
                }
            }
            
            // Get target agent info
            const targetAgent = this.agents.find(a => a.id === this.selectedAgentId);
            const targetAgentName = targetAgent?.name || 'Agent';
            
            // Add agent response or error
            // --- Extract explorer directive (client-side fallback) ---
            // The server normally strips the directive and puts it in
            // metadata.explorer_action.  If it arrives in the raw text
            // (e.g. via the JSON-RPC status-update path which doesn't
            // carry per-message metadata) we extract it here.
            let explorerAction = agentMsgMetadata?.explorer_action || null;
            if (!explorerAction) {
                // Check the first text part in streamedContentParts
                const textPart = streamedContentParts.find(p => p.type === 'text');
                if (textPart && textPart.text) {
                    const { cleanText, action } = extractExplorerDirective(textPart.text);
                    if (action) { textPart.text = cleanText; explorerAction = action; }
                }
                // Also check plain agentResponse
                if (!explorerAction && agentResponse) {
                    const { cleanText, action } = extractExplorerDirective(agentResponse);
                    if (action) { agentResponse = cleanText; explorerAction = action; }
                }
            }

            const _baseMeta = {
                agent_id: this.selectedAgentId,
                agent_name: targetAgentName,
                ...(agentMsgMetadata || {}),
                ...(explorerAction ? { explorer_action: explorerAction } : {}),
            };

            if (taskError) {
                // Task failed - show error message
                this.messages.push({
                    role: 'error',
                    content: [{ type: 'text', text: taskError }],
                    timestamp: new Date().toISOString(),
                    metadata: { ..._baseMeta, task_status: taskStatus || 'failed', error: true },
                });
                await this._loadTasks();
            } else if (!hasInjectedMessages) {
                // Only add a synthetic message if no event_type=message events
                // were injected (i.e. JSON-RPC status-update path only)
                if (streamedContentParts.length > 0) {
                    // Rich content with MCP UI / A2UI parts
                    this.messages.push({
                        role: 'agent',
                        content: streamedContentParts,
                        timestamp: new Date().toISOString(),
                        metadata: {
                            ..._baseMeta,
                            has_ui_content: streamedContentParts.some(p => p.type === 'mcp_ui' || p.type === 'a2ui'),
                        },
                    });
                } else if (agentResponse) {
                    // Plain text response
                    this.messages.push({
                        role: 'agent',
                        content: [{ type: 'text', text: agentResponse }],
                        timestamp: new Date().toISOString(),
                        metadata: { ..._baseMeta },
                    });
                }
            } else if (taskStatus === 'failed') {
                // No explicit error but task status is failed
                this.messages.push({
                    role: 'error',
                    content: [{ type: 'text', text: 'Task failed without error details' }],
                    timestamp: new Date().toISOString(),
                    metadata: {
                        agent_id: this.selectedAgentId,
                        agent_name: targetAgentName,
                        task_status: 'failed',
                        error: true,
                    },
                });
                await this._loadTasks();
            }

            // Notify parent that agent data may have changed
            if (hasInjectedMessages || streamedContentParts.length > 0 || agentResponse) {
                this._notifyDataChanged();
            }
            
        } catch (err) {
            console.error('[A2UIChat] Stream error:', err);
            
            // Capture error in Sentry
            captureError(err, {
                tags: { operation: 'stream', agent_id: this.selectedAgentId },
                extra: {
                    workspace_id: this.options.workspaceId,
                    canvas_id: this.options.canvasId,
                    task_id: this.currentTaskId,
                    persona_id: this.selectedPersonaId,
                }
            });
            
            // Get target agent name for error display
            const targetAgentName = this.agents.find(a => a.id === this.selectedAgentId)?.name || 'Agent';
            
            // Add error message with agent attribution
            this.messages.push({
                role: 'error',
                content: [{ type: 'text', text: err.message }],
                timestamp: new Date().toISOString(),
                metadata: {
                    agent_id: this.selectedAgentId,
                    agent_name: targetAgentName,
                    error: true,
                },
            });
            
            if (this.options.onError) {
                this.options.onError(err);
            }
        } finally {
            this.isStreaming = false;
            this._renderMessages();
        }
    }
    
    /**
     * Notify the parent application that the agent may have changed data.
     * The parent can use this to reload graph content, refresh tables, etc.
     */
    _notifyDataChanged() {
        console.log('[A2UIChat] Notifying data changed');
        if (typeof this.options.onDataChanged === 'function') {
            try {
                this.options.onDataChanged({
                    workspaceId: this.options.workspaceId,
                    canvasId: this.options.canvasId,
                    taskId: this.currentTaskId,
                    agentId: this.selectedAgentId,
                });
            } catch (err) {
                console.warn('[A2UIChat] onDataChanged callback error:', err);
            }
        }
        // Also dispatch a DOM event so non-callback consumers can listen
        if (this.container) {
            this.container.dispatchEvent(new CustomEvent('a2ui-data-changed', {
                bubbles: true,
                detail: {
                    workspaceId: this.options.workspaceId,
                    canvasId: this.options.canvasId,
                    taskId: this.currentTaskId,
                    agentId: this.selectedAgentId,
                },
            }));
        }
    }

    /**
     * Normalize messages (parts→content) and pair adjacent tool_call + tool_response
     * parts into a single message so they render as one compact row.
     * Non-tool agent messages (text) are kept as separate bubbles.
     */
    _pairToolMessages(messages) {
        if (!messages || messages.length === 0) return [];
        const result = [];
        for (const msg of messages) {
            // Normalize: ensure content exists (backend history uses 'parts')
            const normalized = { ...msg };
            if (!normalized.content && normalized.parts) {
                normalized.content = normalized.parts;
            }

            // --- Tool visibility filter ---
            // Hide ALL tool messages (tool_call and tool_response) so the
            // user only sees the final agent text response.
            const msgKind = normalized.metadata?.kind;
            if (msgKind === 'tool_call' || msgKind === 'tool_response') continue;

            // Also hide messages with empty / whitespace-only text content
            if (normalized.role === 'agent' && Array.isArray(normalized.content)) {
                const hasVisibleContent = normalized.content.some(p => {
                    const k = p.type || p.kind || 'text';
                    if (k === 'tool_call' || k === 'tool_response') return false;
                    if (k === 'text') return (p.text || '').trim().length > 0;
                    return true; // mcp_ui, a2ui, image, file, etc.
                });
                if (!hasVisibleContent) continue;
            }

            // For agent messages, check if we can pair tool parts
            if (normalized.role === 'agent' && Array.isArray(normalized.content)) {
                const parts = normalized.content;
                // Check if ALL parts are tool_call or tool_response (pure tool message)
                const allTool = parts.every(p => {
                    const k = p.type || p.kind || 'text';
                    return k === 'tool_call' || k === 'tool_response';
                });
                if (allTool && parts.length > 0) {
                    const prev = result[result.length - 1];
                    // If previous message is also a pure-tool agent message, merge into it
                    if (prev && prev.role === 'agent' && prev._toolOnly) {
                        const prevContent = Array.isArray(prev.content) ? prev.content : [];
                        prev.content = [...prevContent, ...parts];
                        continue;
                    }
                    // Otherwise start a new tool-only message
                    normalized._toolOnly = true;
                }
            }
            result.push(normalized);
        }
        return result;
    }

    /**
     * Process stream parts into rich content parts array
     * Handles text, mcp_ui, a2ui, file, data parts
     */
    _processStreamParts(parts, contentParts) {
        parts.forEach(p => {
            const partType = p.kind || p.type || 'text';
            if (partType === 'text') {
                const existing = contentParts.find(c => c.type === 'text');
                if (existing) {
                    existing.text += p.text || '';
                } else {
                    contentParts.push({ type: 'text', text: p.text || '' });
                }
            } else if (partType === 'tool_call') {
                contentParts.push({
                    type: 'tool_call',
                    text: p.text || '',
                    tool_name: p.tool_name || '',
                    tool_args: p.tool_args || {},
                });
            } else if (partType === 'tool_response') {
                contentParts.push({
                    type: 'tool_response',
                    text: p.text || '',
                    tool_name: p.tool_name || '',
                    tool_result: p.tool_result || '',
                });
            } else if (partType === 'mcp_ui' || partType === 'ui') {
                contentParts.push({
                    type: 'mcp_ui',
                    ui_resource: p.ui_resource || p.resource,
                    name: p.name,
                    id: p.id
                });
            } else if (partType === 'a2ui') {
                contentParts.push({
                    type: 'a2ui',
                    payload: p.payload,
                    catalog: p.catalog,
                    name: p.name,
                    id: p.id
                });
            } else if (partType === 'file') {
                contentParts.push({ type: 'file', ...p.file });
            } else if (partType === 'data') {
                contentParts.push({ type: 'data', data: p.data });
            }
        });
    }
    
    /**
     * Extract plain text from message content/parts array
     * Matches explorer-tasks extractMessageContent pattern
     */
    _extractMessageContentText(content) {
        if (!content) return '';
        if (typeof content === 'string') return content;
        if (Array.isArray(content)) {
            return content
                .map(p => p.text || p.content || '')
                .filter(Boolean)
                .join('\n');
        }
        return '';
    }
    
    /**
     * Make API request
     */
    async _apiRequest(endpoint, options = {}) {
        const url = `${this.options.apiEndpoint}${endpoint}`;
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers,
        };
        
        if (this.options.accessToken) {
            headers['Authorization'] = `Bearer ${this.options.accessToken}`;
        }
        
        addBreadcrumb({
            category: 'api',
            message: `API Request: ${options.method || 'GET'} ${endpoint}`,
            data: { endpoint, method: options.method || 'GET' }
        });
        
        try {
            const response = await fetch(url, {
                ...options,
                headers,
            });
            
            if (!response.ok) {
                const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
                error.status = response.status;
                error.endpoint = endpoint;
                throw error;
            }
            
            return response.json();
        } catch (error) {
            captureError(error, {
                tags: { operation: 'apiRequest' },
                extra: {
                    endpoint,
                    method: options.method || 'GET',
                    workspace_id: this.options.workspaceId,
                    canvas_id: this.options.canvasId,
                    status: error.status,
                }
            });
            throw error;
        }
    }
    
    // ========================================
    // PUBLIC METHODS
    // ========================================
    
    /**
     * Set layout mode
     */
    setLayoutMode(mode) {
        this.options.layoutMode = mode;
        const canvasEl = this.container.querySelector('.a2ui-canvas');
        if (canvasEl) {
            canvasEl.setAttribute('data-layout', mode);
        }
    }
    
    /**
     * Set theme
     */
    setTheme(theme) {
        this.options.theme = theme;
        const canvasEl = this.container.querySelector('.a2ui-canvas');
        if (canvasEl) {
            canvasEl.setAttribute('data-theme', theme);
        }
    }
    
    /**
     * Set canvas ID
     */
    setCanvasId(canvasId) {
        this.options.canvasId = canvasId;
        // Reload artifacts for new canvas
        this._loadArtifacts();
    }
    
    /**
     * Clear conversation / history
     * If on Main tab, clears all history
     * If on a task tab, closes that tab and switches to Main
     */
    clearConversation() {
        if (this.activeTabId === 'main') {
            // Clear main tab messages
            this.messages = [];
            this.currentTaskId = null;
            this._saveState();
            this._renderMessages();
        } else {
            // Close current task tab and switch to main
            this._closeTab(this.activeTabId, true);
        }
        this._renderTasks();

        // Notify host (e.g. ExplorerChat) so it can reset the cursor / graph
        if (typeof this.options.onReset === 'function') {
            try { this.options.onReset(); } catch (e) {
                console.warn('[A2UIChat] onReset callback error:', e);
            }
        }
    }
    
    /**
     * Refresh all data
     */
    async refresh() {
        await Promise.all([
            this._loadTasks(),
            this._loadArtifacts(),
            this._loadAgents(),
        ]);
    }
    
    /**
     * Cleanup
     */
    destroy() {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
        if (this.container) {
            safeSetInnerHTML(this.container, '');
        }
    }
    
    // ========================================
    // AUTOCOMPLETE SUGGESTIONS
    // ========================================
    
    /**
     * Handle input for autocomplete suggestions (called by EntityInput onSearch)
     */
    async _handleSuggestionInput(query) {
        // Need at least 2 characters and workspace
        // Allow empty string apiEndpoint (for proxied dev environment)
        if (!query || query.length < 2 || !this.options.workspaceId || this.options.apiEndpoint === null || this.options.apiEndpoint === undefined) {
            if (this.entityInput) {
                this.entityInput.setSuggestions([]);
            }
            if (this.emptyEntityInput) {
                this.emptyEntityInput.setSuggestions([]);
            }
            return;
        }
        
        try {
            const response = await this._apiRequest(
                `/workspaces/${this.options.workspaceId}/search/suggest?query=${encodeURIComponent(query)}&limit=8`
            );
            
            if (response && response.length > 0) {
                if (this.entityInput) {
                    this.entityInput.setSuggestions(response);
                }
                if (this.emptyEntityInput) {
                    this.emptyEntityInput.setSuggestions(response);
                }
            } else {
                if (this.entityInput) {
                    this.entityInput.setSuggestions([]);
                }
                if (this.emptyEntityInput) {
                    this.emptyEntityInput.setSuggestions([]);
                }
            }
        } catch (err) {
            console.warn('[A2UIChat] Failed to load suggestions:', err);
            if (this.entityInput) {
                this.entityInput.setSuggestions([]);
            }
            if (this.emptyEntityInput) {
                this.emptyEntityInput.setSuggestions([]);
            }
        }
    }
    
    /**
     * Get icon for suggestion type
     */
    _getSuggestionIcon(type) {
        const icons = {
            'node': 'circle-dot',
            'concept': 'tag',
            'type': 'layers',
            'edge_type': 'arrow-right-left',
            'taxonomy_resource': 'tags',
            'ontology_type': 'git-branch',
        };
        return icons[type] || 'file';
    }
}

export default A2UIChat;
